import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import * as THREE from "three";
import { apiFetch } from "../apiBase";
import { generateWaveConfig, generateArenaObstacles, generateEnemyTanks, generateGridObstacles, adaptiveDifficultyMultiplier, WaveConfig, WaveShaping, ArenaShape, TerrainConfig, TERRAIN_THEMES, getRandomTerrain } from "../waveGenerator";
import { generateStoryArenaConfig, generateStoryEnemies, getStoryWaveInfo, getTotalWaves, CHAPTER_TERRAIN_MAP, BossConfig, BOSS_CONFIGS, generateDungeonRoomEnemies, getDungeonRoomArenaConfig } from "../story/storyWaveGenerator";
import { DungeonRoom, DoorDirection } from "../story/dungeonTypes";
import { chapterHasDungeon } from "../story/dungeonConfigs";
import { getDungeonSpawnPosition, shouldSpawnEnemiesInRoom } from "../story/dungeonSpawnUtils";
import { useProgress } from "./useProgress";
import { useDifficulty, DIFFICULTY_CONFIGS } from "./useDifficulty";
import { useDailyMissions } from "./useDailyMissions";
import { useInfiniteRun } from "./useInfiniteRun";
import {
  arenaSurface,
  clearArenaSurface,
  hazardsPresent,
  isInsideHazard,
  planArenaTerrain,
  pushOutOfHazards,
  setArenaSurface,
} from "../terrain/arenaSurface";
import { getWaveKind, isBossWave } from "../infinite/milestones";
import {
  getTankSurfaceStatus,
  PLAYER_SURFACE_ID,
  releaseTankSurface,
  resetTankSurfaces,
} from "../terrain/tankSurface";
import { deterministicSeed as infiniteSeed } from "../infinite/rng";
import type { EnemyKind } from "../infinite/contracts";
import { useColyseus } from "../colyseus/useColyseus";
import { multiplayerSpawnZones } from "@shared/multiplayerArena";
import { useAudio } from "./useAudio";
import { ActiveAbility, MineData, FreezePathData, FogData } from "../story/bossAbilities";
import { resetAIState } from "../genaiClient";
import type { CosmeticEffectType } from "@/lib/effects/effectProfiles";
import type { TankSpecial } from "../types/tanks";
import {
  getPlayerTankSpecial,
  getPierceCount,
  applyShieldReduction,
  getRebirthHealth,
  REBIRTH_INVULN_MS,
  BURN_DURATION_MS,
  BURN_TICK_MS,
} from "../combat/tankSpecials";

/** Nome que aparece no aviso de eliminação quando quem matou foi o terreno. */
const TERRAIN_KILLER_NAME = "🌋 Terreno";

/**
 * Quem derrubou o inimigo.
 *
 * A distinção existe por uma razão só: um bot que se afoga na própria lava não
 * é um abate do jogador. Creditá-lo resolveria contratos de alvo prioritário
 * por acidente, entraria nas missões diárias como dano causado e pagaria
 * recompensa — três mentiras pequenas que somadas fazem o modo parecer
 * quebrado. Tudo o que **não** é crédito continua idêntico, e é de propósito:
 * o fim de onda, a contagem de vivos do Battle Royale, a vitória de capítulo e
 * o alvo do espectador têm de acontecer mesmo quando quem matou foi o chão.
 */
export type EnemyDamageSource = "player" | "terrain";

/**
 * Folga de terreno em volta de cada nascimento multijogador.
 *
 * Uma largura de tanque e meia. Precisa combinar exatamente com a do servidor —
 * as duas pontas geram a arena da mesma semente, e o que muda a lista de
 * lugares proibidos muda o sorteio inteiro.
 */
const MP_SPAWN_MARGIN = 2;

export type GamePhase = "menu" | "playing" | "paused" | "gameOver" | "waveComplete" | "story" | "storyPrologue" | "storyIntro" | "storyPlaying" | "storyVictory" | "genAIVictory";

export type GameMode = "roguelike" | "battleRoyale" | "genai";

export interface DungeonRoomEntryContext {
  fromRoomId?: string | null;
  entryDirection?: DoorDirection | null;
  roomCompleted?: boolean;
  isChapterStart?: boolean;
  forceRespawn?: boolean;
}

export type GenAIConfigType = {
  mode: "player-vs-ai" | "ai-vs-ai";
  aiProvider1: "openai" | "gemini";
  aiProvider2?: "openai" | "gemini";
  personality1: "aggressive" | "defensive" | "strategic";
  personality2?: "aggressive" | "defensive" | "strategic";
  terrain: string;
  arenaSize: number;
};

export interface TankData {
  id: string;
  position: THREE.Vector3;
  rotation: number;
  health: number;
  maxHealth: number;
  color: string;
  isPlayer: boolean;
  isAlive: boolean;
  name: string;
  lastFireTime: number;
  enemyType?: "normal" | "heavy" | "fast" | "sniper" | "boss";
  speed?: number;
  damage?: number;
  fireRate?: number;
  shotCount?: number;
  size?: number;
  aiLevel?: number;
  visualType?: "tank" | "drone";
  skinId?: string | null;
  effectId?: string | null;
  activeEffect?: ActiveEffect | null;
  shieldHits?: number;
  /** Modificador de bloco "voláteis": deixa uma explosão ao morrer. */
  explodesOnDeath?: boolean;
}

export interface BulletData {
  id: string;
  position: THREE.Vector3;
  direction: THREE.Vector3;
  ownerId: string;
  speed: number;
  damage: number;
  createdAt: number;
  size?: number;
  effectType?: ItemType;
  projectileStyle?: "orb" | "laser";
  cosmeticEffectId?: string | null;
  cosmeticEffectType?: CosmeticEffectType | null;
  /** Poder de linhagem do tanque que disparou (explosive/piercing/homing). */
  tankSpecial?: TankSpecial;
  tankSpecialPower?: number;
  /** Perfurações restantes (piercing). */
  pierceLeft?: number;
  /** Inimigos já atingidos por esta bala (piercing não re-acerta). */
  hitIds?: string[];
  /**
   * [NETCODE] Bala sincronizada pelo servidor (multiplayer): o cliente avança
   * localmente (dead reckoning) e corrige suavemente rumo à última posição do
   * servidor. Colisões locais não são processadas — o servidor é a autoridade.
   */
  netSynced?: boolean;
  /** Última posição conhecida do servidor (atualizada a cada patch). */
  serverX?: number;
  serverZ?: number;
  /** Timestamp local (ms) em que serverX/serverZ foram atualizados. */
  serverUpdatedAt?: number;
}

export interface ObstacleData {
  id: string;
  position: THREE.Vector3;
  size: THREE.Vector3;
  type: "destructible" | "indestructible";
  health: number;
  maxHealth: number;
  styleVariant?: number;
  surfaceSeed?: number;
}

export interface ExplosionData {
  position: { x: number; y: number; z: number };
  color: string;
  createdAt: number;
  cosmeticEffectId?: string | null;
  cosmeticEffectType?: CosmeticEffectType | null;
  source?: "hit" | "kill" | "ability" | "environment";
  ownerId?: string | null;
}

type ExplosionOptions = {
  cosmeticEffectId?: string | null;
  cosmeticEffectType?: CosmeticEffectType | null;
  source?: "hit" | "kill" | "ability" | "environment";
  ownerId?: string | null;
};

export type ItemType =
  | "intense"
  | "freeze"
  | "shield"
  | "heal"
  | "turbo"
  | "invisibility"
  | "emp"
  | "vampire"
  | "airstrike"
  | "minigun"
  | "magnet"
  | "gravityWell"
  | "timeDilation"
  | "echoShield"
  | "decoyHologram"
  | "shockwave";

export interface ItemData {
  id: string;
  type: ItemType;
  position: THREE.Vector3;
  createdAt: number;
}

export interface ActiveEffect {
  type: ItemType;
  expiresAt: number;
  shieldHits?: number;
  origin?: { x: number; z: number };
  decoyPosition?: { x: number; z: number };
}

// Interface para rastrear padrões do jogador (GenAI Learning)
export interface PlayerPatternTracking {
  // Movimento
  totalMovementDistance: number;
  movementSamples: number;
  lastPosition: { x: number; z: number } | null;
  
  // Tiros
  totalShots: number;
  matchStartTime: number;
  
  // Esquivas (balas que passaram perto mas não acertaram)
  nearMisses: number;
  totalDodgeOpportunities: number;
  
  // Agressividade (distância ao inimigo quando atira)
  attackDistances: number[];
  
  // Tempo em diferentes distâncias
  timeNearEnemy: number;  // < 15 unidades
  timeFarFromEnemy: number;  // > 30 unidades
  timeMediumRange: number;  // 15-30 unidades
}

export const ITEM_CONFIG: Record<ItemType, { 
  name: string; 
  color: string; 
  duration: number; 
  description: string;
  icon: string;
}> = {
  intense: { 
    name: "Tiros Intensos", 
    color: "#ff6b00", 
    duration: 5000, 
    description: "+50% dano",
    icon: "🔥"
  },
  freeze: { 
    name: "Tiros Congelantes", 
    color: "#00d4ff", 
    duration: 5000, 
    description: "Congela inimigos",
    icon: "❄️"
  },
  shield: { 
    name: "Campo de Força", 
    color: "#a855f7", 
    duration: 6000, 
    description: "Bloqueia 4 tiros",
    icon: "🛡️"
  },
  heal: { 
    name: "Cura", 
    color: "#22c55e", 
    duration: 0, 
    description: "+30 vida",
    icon: "💚"
  },
  turbo: { 
    name: "Turbo", 
    color: "#fbbf24", 
    duration: 4000, 
    description: "+60% velocidade",
    icon: "⚡"
  },
  // Itens exclusivos do Battle Royale
  invisibility: {
    name: "Invisibilidade",
    color: "#8b5cf6",
    duration: 4000,
    description: "Fica invisível",
    icon: "👻"
  },
  emp: {
    name: "Pulso EMP",
    color: "#3b82f6",
    duration: 0,
    description: "Desabilita inimigos próximos",
    icon: "📡"
  },
  vampire: {
    name: "Vampiro",
    color: "#dc2626",
    duration: 6000,
    description: "Rouba vida ao atacar",
    icon: "🦇"
  },
  airstrike: {
    name: "Ataque Aéreo",
    color: "#f97316",
    duration: 0,
    description: "Bombardeio em área",
    icon: "🎯"
  },
  minigun: {
    name: "Metralhadora",
    color: "#6b7280",
    duration: 5000,
    description: "Tiros rápidos",
    icon: "🔫"
  },
  magnet: {
    name: "Ímã",
    color: "#ec4899",
    duration: 5000,
    description: "Atrai itens próximos",
    icon: "🧲"
  },
  gravityWell: {
    name: "Poço Gravitacional",
    color: "#7c3aed",
    duration: 4200,
    description: "Puxa inimigos e projéteis",
    icon: "🌀"
  },
  timeDilation: {
    name: "Dilatação Temporal",
    color: "#38bdf8",
    duration: 4500,
    description: "Desacelera inimigos e balas",
    icon: "⏳"
  },
  echoShield: {
    name: "Escudo Eco",
    color: "#22d3ee",
    duration: 7000,
    description: "Reflete o próximo tiro",
    icon: "🪞"
  },
  decoyHologram: {
    name: "Isca Holográfica",
    color: "#a78bfa",
    duration: 5000,
    description: "Atrai mira inimiga",
    icon: "👥"
  },
  shockwave: {
    name: "Onda de Choque",
    color: "#f43f5e",
    duration: 0,
    description: "Empurra inimigos e projéteis",
    icon: "💥"
  },
};

interface RocketanksState {
  phase: GamePhase;
  gameMode: GameMode;
  playerTank: TankData | null;
  enemyTanks: TankData[];
  bullets: BulletData[];
  obstacles: ObstacleData[];
  explosions: ExplosionData[];
  items: ItemData[];
  heldItem: ItemType | null;
  activeEffect: ActiveEffect | null;
  frozenEnemies: Map<string, number>;
  score: number;
  wave: number;
  waveConfig: WaveConfig | null;
  arenaSize: number;
  arenaShape: ArenaShape;
  terrain: TerrainConfig;
  killsThisWave: number;
  pausedBy: string | null;
  previousPhase: GamePhase | null;
  pausedAt: number | null;
  isMultiplayer: boolean;
  
  // Battle Royale específico
  brCurrentArenaSize: number;
  brTargetArenaSize: number;
  brIsShrinking: boolean;
  brElectricWallActive: boolean;
  brAliveCount: number;
  brPlayerRank: number | null;
  brBotCount: number;
  brIsSpectating: boolean;
  brSpectatingTargetId: string | null;
  brEliminationFeed: { eliminatedName: string; killerName: string; timestamp: number }[];
  /**
   * Config da partida BR em curso. Guardada para o "jogar de novo" da tela de
   * fim de partida repetir a mesma arena (bots, tamanho, terreno e densidade)
   * sem passar pelo menu de configuração de novo.
   */
  brLastConfig: { botCount: number; arenaSize: number; terrainTheme?: string; blockDensity?: number } | null;
  
  // [MULTIPLAYER-SPECTATOR] Modo espectador para multiplayer
  multiplayerIsSpectating: boolean;
  multiplayerSpectatingTargetId: string | null;
  remotePlayersMap: Map<string, { id: string; sessionId: string; name: string; position: { x: number; y: number; z: number }; isAlive: boolean; health: number; color: string }>;
  
  // GenAI Mode específico
  isAiVsAi: boolean;
  genAIProvider1: "openai" | "gemini" | null;
  genAIProvider2: "openai" | "gemini" | null;
  genAIPersonality: "aggressive" | "defensive" | "strategic" | null;
  genAIPersonality2: "aggressive" | "defensive" | "strategic" | null;
  genAIThought: string;
  genAIThought2: string;
  genAIThoughtIsOriginal: boolean;
  genAIThought2IsOriginal: boolean;
  genAIProviderSwitched1: boolean;  // Se provider 1 foi trocado por rate limit
  genAIProviderSwitched2: boolean;  // Se provider 2 foi trocado por rate limit
  genAIWinner: { name: string; isPlayer: boolean; personality: string; provider?: string } | null;
  genAIMatchSessionId: string | null;  // ID único da partida para registro
  genAIPlayerPatterns: PlayerPatternTracking;  // Rastreamento de padrões do jogador
  setGenAIThought: (thought: string, aiIndex?: 1 | 2, isOriginal?: boolean, providerSwitched?: boolean) => void;
  setGenAIWinner: (winner: { name: string; isPlayer: boolean; personality: string; provider?: string } | null) => void;
  trackGenAIPlayerAction: (action: "shot" | "move" | "nearMiss" | "dodged", data?: any) => void;
  submitGenAIMatchResult: (aiWon: boolean, aiKills: number, playerKills: number) => Promise<void>;
  
  // Modo História - campos adicionais
  storyChapterId: number | null;
  storyWaveIndex: number;
  storyTotalWaves: number;
  storyBossPhase: number;
  storyPendingDialogue: string | null;
  storyNoHealing: boolean;
  
  // Timer de Proteção - Capítulo 3: proteger Dra. Elise
  storyProtectionActive: boolean;      // true quando missão de proteção está ativa
  storyProtectionTimeLeft: number;     // tempo restante em segundos
  storyProtectionStartTime: number;    // timestamp de início
  storyProtectionDuration: number;     // duração total em segundos (60s para cap 3)
  storyProtectionTarget: string | null; // nome do alvo protegido (ex: "Dra. Elise")
  storyProtectionDialogueTriggers: { time: number; nodeId: string }[]; // diálogos por tempo
  
  // Sistema de Dungeon
  currentDungeonRoomId: string | null;        // ID da sala atual do dungeon
  dungeonEntryDirection: "NORTH" | "SOUTH" | "EAST" | "WEST" | null; // Direção que o jogador entrou
  dungeonRoomWaveIndex: number;               // Índice da wave atual na sala
  dungeonDoorsUnlocked: boolean;              // Se as portas estão desbloqueadas
  dungeonVisitedRooms: string[];              // Salas já visitadas
  dungeonCollectedRewards: string[];          // Recompensas coletadas (por room ID)
  
  // Modo Astra - habilidade especial do modo história
  astraUnlocked: boolean;       // true quando Astra foi desbloqueado (após intro na boss wave)
  astraCharge: number;          // 0-100, carrega com o tempo
  astraActive: boolean;         // true quando Modo Astra está ativo
  astraActiveUntil: number;     // timestamp de quando termina
  astraCooldownUntil: number;   // timestamp de quando pode usar novamente (superaquecimento)
  astraUsedThisChapter: number; // quantas vezes usou neste capítulo
  
  moveDirection: THREE.Vector2;
  isFiring: boolean;
  aimDirection: THREE.Vector2;
  
  isCharging: boolean;
  chargeStartTime: number;
  chargeLevel: number;
  
  // Feedback visual de dano
  lastDamageTime: number;
  lastDamageAmount: number;

  // Poderes passivos do tanque selecionado (modos locais)
  lastPlayerShotAt: number;       // timestamp do último tiro do jogador
  playerStealthed: boolean;       // true quando o poder stealth está ativo
  playerStealthRadius: number;    // raio de detecção enquanto furtivo
  setPlayerStealth: (stealthed: boolean, radius?: number) => void;

  // [BESTIÁRIO] Fênix (rebirth) e sopro do Dragão (burn) nos modos locais
  playerRebirthUsed: boolean;        // renascimento já consumido nesta partida
  playerRebirthInvulnUntil: number;  // invulnerável até este timestamp
  lastRebirthAt: number;             // timestamp do último renascimento (VFX)
  enemyBurns: Record<string, { until: number; nextTickAt: number; tickDamage: number }>;
  igniteEnemy: (id: string, tickDamage: number) => void;
  tickEnemyBurns: () => void;

  setPhase: (phase: GamePhase) => void;
  setGameMode: (mode: GameMode) => void;
  startGame: () => void;
  startBattleRoyale: (botCount?: number, arenaSize?: number, terrainTheme?: string, blockDensity?: number) => void;
  /** Repete a última partida BR com a mesma configuração (botão da tela de fim). */
  restartBattleRoyale: () => void;
  startGenAI: (config: GenAIConfigType) => void;
  endGame: (won: boolean) => void;
  restartGame: () => void;
  nextWave: () => void;
  togglePause: (playerId?: string) => void;
  setPaused: (paused: boolean, playerId?: string) => void;
  setMultiplayer: (isMultiplayer: boolean) => void;
  
  // Battle Royale actions
  updateBRArena: () => void;
  shrinkBRArena: (shrinkDelta: number) => void;
  setBRPlayerRank: (rank: number | null) => void;
  updateBRAliveCount: () => void;
  startBRSpectating: () => void;
  stopBRSpectating: () => void;
  nextBRSpectateTarget: () => void;
  updateBRSpectateTarget: () => void;
  addBREliminationFeed: (eliminatedName: string, killerName: string) => void;
  
  // [MULTIPLAYER-SPECTATOR] Funções para espectador multiplayer
  startMultiplayerSpectating: () => void;
  stopMultiplayerSpectating: () => void;
  setMultiplayerSpectateTarget: (targetId: string) => void;
  nextMultiplayerSpectateTarget: () => void;
  updateMultiplayerSpectateTarget: () => void;
  
  setMoveDirection: (dir: THREE.Vector2) => void;
  setIsFiring: (firing: boolean) => void;
  setAimDirection: (dir: THREE.Vector2) => void;
  
  startCharging: () => void;
  updateChargeLevel: () => void;
  releaseCharge: () => number;
  cancelCharge: () => void;
  
  updatePlayerPosition: (pos: THREE.Vector3, rotation: number) => void;
  updatePlayerHealth: (health: number) => void;
  
  addBullet: (bullet: BulletData) => void;
  removeBullet: (id: string) => void;
  
  /**
   * `source` diz quem causou o dano. "player" credita (pontos, contratos,
   * missões); "terrain" só mata. Tudo o que vem depois da morte — fim de onda,
   * contagem de vivos do BR, vitória de capítulo — é o mesmo nos dois.
   */
  damageEnemy: (id: string, damage: number, source?: EnemyDamageSource) => void;
  /** Atalho de leitura para o dano de lava e chamas. */
  damageEnemyByTerrain: (id: string, damage: number) => void;
  // [BR][PERF] Dano da zona a TODOS os inimigos fora dela em um único set()
  applyBRZoneDamage: (halfSize: number, damage: number) => void;
  removeEnemy: (id: string) => void;
  updateEnemyPosition: (id: string, pos: THREE.Vector3) => void;
  
  damageObstacle: (id: string, damage: number) => void;
  removeObstacle: (id: string) => void;
  
  addScore: (points: number) => void;
  
  addExplosion: (position: { x: number; y: number; z: number }, color: string, options?: ExplosionOptions) => void;
  clearExplosions: () => void;
  
  addItem: (item: ItemData) => void;
  removeItem: (id: string) => void;
  collectItem: (id: string) => void;
  useHeldItem: () => void;
  setActiveEffect: (effect: ActiveEffect | null) => void;
  setHeldItem: (item: ItemType | null) => void;
  syncMultiplayerItems: (items: ItemData[]) => void;
  syncMultiplayerPowerupState: (data: { heldItem: ItemType | null; activeEffect: ActiveEffect | null }) => void;
  freezeEnemy: (enemyId: string, duration: number) => void;
  isEnemyFrozen: (enemyId: string) => boolean;
  blockBulletWithShield: () => boolean;
  spawnRandomItem: () => void;
  compensateTimeJump: (deltaMs: number) => void;
  
  initializeGame: (terrainOverride?: TerrainConfig) => void;
  initializeWave: (waveNumber: number, terrainOverride?: TerrainConfig) => void;
  initializeMultiplayerGame: (spawnPosition: { x: number; y: number; z: number }, playerColor: string, playerName: string, seed?: number, serverTerrain?: string, serverArenaSize?: number, serverObstacles?: ObstacleData[]) => void;
  setSelectedTerrain: (terrain: TerrainConfig | null) => void;
  selectedTerrain: TerrainConfig | null;
  
  // Modo História - actions
  initializeStoryChapter: (chapterId: number, options?: { dungeonMode?: boolean }) => void;
  advanceStoryWave: () => string | null;
  spawnStoryWaveEnemies: () => void;
  getStoryBossConfig: () => BossConfig | null;
  advanceBossPhase: () => boolean;
  clearStoryPendingDialogue: () => void;
  
  // Dungeon System - actions
  loadDungeonRoom: (room: DungeonRoom, waveIndex?: number, entryContext?: DungeonRoomEntryContext | null) => void;
  advanceDungeonWave: () => boolean;  // Retorna true se há mais waves na sala
  unlockDungeonDoors: () => void;
  enterDungeonRoom: (roomId: string) => void;
  resetDungeonState: () => void;
  isDungeonRoomCleared: () => boolean;
  
  // Timer de Proteção - actions
  startProtectionTimer: (duration: number, target: string, dialogueTriggers?: { time: number; nodeId: string }[]) => void;
  updateProtectionTimer: () => { completed: boolean; dialogueToShow: string | null };
  stopProtectionTimer: () => void;
  
  // Modo Astra - actions
  unlockAstra: () => void;
  updateAstraCharge: (delta: number) => void;
  activateAstra: () => boolean;
  deactivateAstra: () => void;
  isAstraReady: () => boolean;
  isAstraOnCooldown: () => boolean;
  getAstraCooldownPercent: () => number;
  
  // Habilidades de Boss - state e actions
  bossAbilities: ActiveAbility[];
  bossMines: (MineData & { id: string; createdAt: number })[];
  bossFreezePaths: (FreezePathData & { bossId: string; expiresAt: number })[];
  bossFogAreas: (FogData & { bossId: string; expiresAt: number })[];
  playerEmpDisabledUntil: number;
  bossCharging: { bossId: string; abilityType: string; startedAt: number; duration: number } | null;
  
  addBossMine: (mine: MineData & { id: string; createdAt: number }) => void;
  removeBossMine: (id: string) => void;
  triggerMine: (id: string) => void;
  addBossFreezePath: (path: FreezePathData & { bossId: string; expiresAt: number }) => void;
  updateBossFreezePath: (bossId: string, newPoint: THREE.Vector3) => void;
  addBossFogArea: (fog: FogData & { bossId: string; expiresAt: number }) => void;
  setPlayerEmpDisabled: (until: number) => void;
  isPlayerEmpDisabled: () => boolean;
  setBossCharging: (charging: { bossId: string; abilityType: string; startedAt: number; duration: number } | null) => void;
  cleanupBossAbilities: () => void;
  isPlayerOnFreezePath: () => boolean;
  isPlayerInFog: () => boolean;
  getPlayerSlowFactor: () => number;
}

function seededRandom(seed: number): () => number {
  let s = seed;
  return () => {
    s = (s * 1103515245 + 12345) & 0x7fffffff;
    return s / 0x7fffffff;
  };
}

function deterministicSeed(...parts: Array<string | number>): number {
  const key = parts.map((part) => String(part)).join("|");
  let hash = 2166136261;
  for (let i = 0; i < key.length; i++) {
    hash ^= key.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  const normalized = hash & 0x7fffffff;
  return normalized === 0 ? 1 : normalized;
}

// Distância a partir da qual uma divergência entre a posição do store e a
// posição física do tanque é tratada como spawn/teleporte intencional.
// Compartilhado com Tank.tsx (snap de spawn) para manterem o mesmo critério.
export const SPAWN_TELEPORT_DISTANCE = 2.5;
export const SPAWN_TELEPORT_DISTANCE_SQ = SPAWN_TELEPORT_DISTANCE * SPAWN_TELEPORT_DISTANCE;

// Configurações do Battle Royale - exportado para uso em outros componentes
export const BR_CONFIG = {
  INITIAL_ARENA_SIZE: 160,
  MIN_ARENA_SIZE: 8, // Espaço mínimo para um duelo final (paridade com o BR multiplayer)
  MAX_ARENA_SIZE: 400,
  SHRINK_PER_ELIMINATION: 1.8,
  SHRINK_SPEED: 0.15,
  WALL_DAMAGE: 5,
  WALL_DAMAGE_INTERVAL: 200,
  // 50 bots = caminho instanciado (INSTANCED_THRESHOLD) — 49 caía por 1 de
  // diferença no caminho de EnemyTank individual, o pior caso de performance.
  DEFAULT_BOT_COUNT: 50,
  MAX_BOT_COUNT: 299,
  // Limites para dispositivos touch/mobile — acima disso a GPU/CPU de
  // celular não sustenta a partida (obstáculos sem instancing, luzes, IA).
  MOBILE_MAX_BOT_COUNT: 99,
  MOBILE_MAX_ARENA_SIZE: 300,
  SHRINK_START_DELAY: 5000,
  SHRINK_PHASE_INTERVAL: 8000,
  SHRINK_PHASE_INTERVAL_MIN: 3000,
  SHRINK_BASE_AMOUNT: 8,
  SHRINK_INCREMENT: 2,
};

/**
 * [BR][PERF] Canal transiente do tamanho vivo da zona do Battle Royale solo.
 *
 * Durante o encolhimento o tamanho muda a cada frame; escrever isso no store
 * re-renderizava a árvore inteira da cena 60×/s. O valor vivo mora aqui
 * (mutável, fora do React) e é lido em useFrame pela parede elétrica, câmera
 * e clamp de movimento; o store recebe commits discretos (~1 unidade) apenas
 * para os consumidores React (fog, HUD).
 */
export const brLiveZone: { current: number; active: boolean } = { current: 0, active: false };

/** Alcance e dano da explosão dos inimigos "voláteis". */
const VOLATILE_BLAST_RADIUS = 7;
const VOLATILE_BLAST_DAMAGE = 16;

/**
 * A Batalha Infinita é o modo solo `roguelike` sem multiplayer.
 *
 * Todo acréscimo do modo (cartas, contratos, terreno, blocos) passa por este
 * teste antes de tocar em qualquer coisa. É o que garante que história, battle
 * royale, GenAI e multiplayer continuem exatamente como estavam.
 */
export function isInfiniteMode(state: { gameMode: GameMode; isMultiplayer: boolean }): boolean {
  return state.gameMode === "roguelike" && !state.isMultiplayer;
}

// [PERF] Teto de balas simultâneas em cena (solo). Acima disso a mais antiga
// que não é do jogador é descartada — visualmente imperceptível, evita o
// colapso de frame com dezenas de bots atirando.
const MAX_ACTIVE_BULLETS = 240;

type RuntimeMode = "roguelike" | "battleRoyale" | "genai" | "multiplayer";
interface MagicItemBalance {
  gravityDuration: number;
  gravityRadius: number;
  gravityPull: number;
  timeDuration: number;
  timeRadius: number;
  timeSlow: number;
  echoDuration: number;
  decoyDuration: number;
  decoyOffset: number;
  shockwaveRadius: number;
  shockwaveForce: number;
  shockwaveFreezeMs: number;
}

const MAGIC_ITEM_BALANCE: Record<RuntimeMode, MagicItemBalance> = {
  roguelike: {
    gravityDuration: 4300,
    gravityRadius: 13,
    gravityPull: 18,
    timeDuration: 5000,
    timeRadius: 14,
    timeSlow: 0.45,
    echoDuration: 7000,
    decoyDuration: 5200,
    decoyOffset: 6,
    shockwaveRadius: 12,
    shockwaveForce: 8.5,
    shockwaveFreezeMs: 700,
  },
  battleRoyale: {
    gravityDuration: 3400,
    gravityRadius: 11,
    gravityPull: 16,
    timeDuration: 3800,
    timeRadius: 12,
    timeSlow: 0.5,
    echoDuration: 6200,
    decoyDuration: 4300,
    decoyOffset: 5.5,
    shockwaveRadius: 10,
    shockwaveForce: 7.5,
    shockwaveFreezeMs: 500,
  },
  genai: {
    gravityDuration: 3800,
    gravityRadius: 12,
    gravityPull: 17,
    timeDuration: 4200,
    timeRadius: 12.5,
    timeSlow: 0.5,
    echoDuration: 6500,
    decoyDuration: 4500,
    decoyOffset: 5.5,
    shockwaveRadius: 11,
    shockwaveForce: 8,
    shockwaveFreezeMs: 550,
  },
  multiplayer: {
    gravityDuration: 3200,
    gravityRadius: 10,
    gravityPull: 15,
    timeDuration: 3500,
    timeRadius: 10,
    timeSlow: 0.55,
    echoDuration: 6000,
    decoyDuration: 4000,
    decoyOffset: 5,
    shockwaveRadius: 9,
    shockwaveForce: 7,
    shockwaveFreezeMs: 450,
  },
};

function getMagicItemBalance(gameMode: GameMode, isMultiplayer: boolean): MagicItemBalance {
  if (isMultiplayer) return MAGIC_ITEM_BALANCE.multiplayer;
  if (gameMode === "battleRoyale") return MAGIC_ITEM_BALANCE.battleRoyale;
  if (gameMode === "genai") return MAGIC_ITEM_BALANCE.genai;
  return MAGIC_ITEM_BALANCE.roguelike;
}

export const useRocketanks = create<RocketanksState>()(
  subscribeWithSelector((set, get) => ({
    phase: "menu",
    gameMode: "roguelike" as GameMode,
    playerTank: null,
    enemyTanks: [],
    bullets: [],
    obstacles: [],
    explosions: [],
    items: [],
    heldItem: null,
    activeEffect: null,
    frozenEnemies: new Map<string, number>(),
    score: 0,
    wave: 1,
    waveConfig: null,
    arenaSize: 40,
    arenaShape: "square" as ArenaShape,
    terrain: TERRAIN_THEMES.industrial,
    killsThisWave: 0,
    pausedBy: null,
    previousPhase: null,
    pausedAt: null,
    isMultiplayer: false,
    selectedTerrain: null,
    
    // Battle Royale - valores iniciais
    brCurrentArenaSize: BR_CONFIG.INITIAL_ARENA_SIZE,
    brTargetArenaSize: BR_CONFIG.INITIAL_ARENA_SIZE,
    brIsShrinking: false,
    brElectricWallActive: false,
    brAliveCount: 0,
    brPlayerRank: null,
    brBotCount: BR_CONFIG.DEFAULT_BOT_COUNT,
    brIsSpectating: false,
    brSpectatingTargetId: null,
    brEliminationFeed: [],
    brLastConfig: null,

    // [MULTIPLAYER-SPECTATOR] Inicializar flags de espectador multiplayer
    multiplayerIsSpectating: false,
    multiplayerSpectatingTargetId: null,
    remotePlayersMap: new Map<string, { id: string; sessionId: string; name: string; position: { x: number; y: number; z: number }; isAlive: boolean; health: number; color: string }>(),
    
    isAiVsAi: false,
    genAIProvider1: null,
    genAIProvider2: null,
    genAIPersonality: null,
    genAIPersonality2: null,
    genAIThought: "...",
    genAIThought2: "...",
    genAIThoughtIsOriginal: false,
    genAIThought2IsOriginal: false,
    genAIProviderSwitched1: false,
    genAIProviderSwitched2: false,
    genAIWinner: null,
    genAIMatchSessionId: null,
    genAIPlayerPatterns: {
      totalMovementDistance: 0,
      movementSamples: 0,
      lastPosition: null,
      totalShots: 0,
      matchStartTime: 0,
      nearMisses: 0,
      totalDodgeOpportunities: 0,
      attackDistances: [],
      timeNearEnemy: 0,
      timeFarFromEnemy: 0,
      timeMediumRange: 0,
    },
    setGenAIThought: (thought: string, aiIndex: 1 | 2 = 1, isOriginal: boolean = false, providerSwitched: boolean = false) => {
      if (aiIndex === 2) {
        set({ genAIThought2: thought, genAIThought2IsOriginal: isOriginal, genAIProviderSwitched2: providerSwitched });
      } else {
        set({ genAIThought: thought, genAIThoughtIsOriginal: isOriginal, genAIProviderSwitched1: providerSwitched });
      }
    },
    setGenAIWinner: (winner) => {
      set({ genAIWinner: winner });
    },
    
    // Rastrear ações do jogador para aprendizado GenAI
    trackGenAIPlayerAction: (action: "shot" | "move" | "nearMiss" | "dodged", data?: any) => {
      const state = get();
      if (state.gameMode !== "genai" || state.isAiVsAi) return; // Só rastreia no modo player vs AI
      
      const patterns = { ...state.genAIPlayerPatterns };
      
      switch (action) {
        case "shot":
          patterns.totalShots++;
          if (data?.distanceToEnemy) {
            patterns.attackDistances.push(data.distanceToEnemy);
          }
          break;
        case "move":
          if (data?.position && patterns.lastPosition) {
            const dx = data.position.x - patterns.lastPosition.x;
            const dz = data.position.z - patterns.lastPosition.z;
            patterns.totalMovementDistance += Math.sqrt(dx * dx + dz * dz);
            patterns.movementSamples++;
          }
          if (data?.position) {
            patterns.lastPosition = { x: data.position.x, z: data.position.z };
          }
          // Rastrear tempo em diferentes distâncias
          if (data?.distanceToEnemy !== undefined) {
            const dt = 0.016; // ~60fps
            if (data.distanceToEnemy < 15) {
              patterns.timeNearEnemy += dt;
            } else if (data.distanceToEnemy > 30) {
              patterns.timeFarFromEnemy += dt;
            } else {
              patterns.timeMediumRange += dt;
            }
          }
          break;
        case "nearMiss":
          patterns.totalDodgeOpportunities++;
          patterns.nearMisses++;
          break;
        case "dodged":
          patterns.totalDodgeOpportunities++;
          break;
      }
      
      set({ genAIPlayerPatterns: patterns });
    },
    
    // Enviar resultado da partida ao servidor para criar memórias
    submitGenAIMatchResult: async (aiWon: boolean, aiKills: number, playerKills: number) => {
      const state = get();
      if (state.gameMode !== "genai" || state.isAiVsAi) return; // Só registra no modo player vs AI
      
      const patterns = state.genAIPlayerPatterns;
      const matchDuration = Date.now() - patterns.matchStartTime;
      
      // Calcular métricas
      const avgMoveSpeed = patterns.movementSamples > 0 
        ? patterns.totalMovementDistance / (matchDuration / 1000) 
        : 0;
      
      const shotFrequency = matchDuration > 1000 
        ? patterns.totalShots / (matchDuration / 1000) 
        : 0;
      
      const dodgeSuccess = patterns.totalDodgeOpportunities > 0 
        ? patterns.nearMisses / patterns.totalDodgeOpportunities 
        : 0.5;
      
      // Calcular distância preferida
      const totalTime = patterns.timeNearEnemy + patterns.timeMediumRange + patterns.timeFarFromEnemy;
      let preferredDistance = "medium";
      if (totalTime > 0) {
        if (patterns.timeNearEnemy > patterns.timeMediumRange && patterns.timeNearEnemy > patterns.timeFarFromEnemy) {
          preferredDistance = "close";
        } else if (patterns.timeFarFromEnemy > patterns.timeMediumRange && patterns.timeFarFromEnemy > patterns.timeNearEnemy) {
          preferredDistance = "far";
        }
      }
      
      // Calcular agressividade baseada nas distâncias de ataque
      let aggressiveness = 0.5;
      if (patterns.attackDistances.length > 0) {
        const avgAttackDistance = patterns.attackDistances.reduce((a, b) => a + b, 0) / patterns.attackDistances.length;
        // Distância menor = mais agressivo
        aggressiveness = Math.max(0, Math.min(1, 1 - (avgAttackDistance - 5) / 40));
      }
      
      // Obter playerId do localStorage ou gerar um
      const playerId = localStorage.getItem("rocketanks_player_id") || `player_${Date.now()}`;
      if (!localStorage.getItem("rocketanks_player_id")) {
        localStorage.setItem("rocketanks_player_id", playerId);
      }
      
      try {
        const response = await apiFetch("/api/genai/match-complete", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            playerId,
            sessionId: state.genAIMatchSessionId,
            aiPersonality: state.genAIPersonality,
            aiWon,
            aiKills,
            playerKills,
            matchDurationMs: matchDuration,
            playerPatterns: {
              avgMoveSpeed,
              shotFrequency,
              preferredDistance,
              dodgeSuccess,
              aggressiveness,
            },
          }),
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log("[GenAI Learning] Match recorded successfully", result.insights ? "with insights" : "");
        }
      } catch (err) {
        console.error("[GenAI Learning] Failed to submit match result:", err);
      }

      // Remover jogador do mapa ao vivo
      apiFetch("/api/genai/player-disconnect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerId }),
      }).catch(() => {});
    },
    
    // Modo História - valores iniciais
    storyChapterId: null,
    storyWaveIndex: 0,
    storyTotalWaves: 0,
    storyBossPhase: 0,
    storyPendingDialogue: null,
    storyNoHealing: false,
    
    // Timer de Proteção - valores iniciais
    storyProtectionActive: false,
    storyProtectionTimeLeft: 0,
    storyProtectionStartTime: 0,
    storyProtectionDuration: 0,
    storyProtectionTarget: null,
    storyProtectionDialogueTriggers: [] as { time: number; nodeId: string }[],
    
    // Sistema de Dungeon - valores iniciais
    currentDungeonRoomId: null,
    dungeonEntryDirection: null,
    dungeonRoomWaveIndex: 0,
    dungeonDoorsUnlocked: false,
    dungeonVisitedRooms: [] as string[],
    dungeonCollectedRewards: [] as string[],
    
    // Modo Astra - valores iniciais
    astraUnlocked: false,
    astraCharge: 0,
    astraActive: false,
    astraActiveUntil: 0,
    astraCooldownUntil: 0,
    astraUsedThisChapter: 0,
    
    // Habilidades de Boss - valores iniciais
    bossAbilities: [] as ActiveAbility[],
    bossMines: [] as (MineData & { id: string; createdAt: number })[],
    bossFreezePaths: [] as (FreezePathData & { bossId: string; expiresAt: number })[],
    bossFogAreas: [] as (FogData & { bossId: string; expiresAt: number })[],
    playerEmpDisabledUntil: 0, // Jogador não pode atirar até este timestamp
    bossCharging: null as { bossId: string; abilityType: string; startedAt: number; duration: number } | null,
    
    moveDirection: new THREE.Vector2(0, 0),
    isFiring: false,
    aimDirection: new THREE.Vector2(0, -1),
    
    isCharging: false,
    chargeStartTime: 0,
    chargeLevel: 0,
    
    // Feedback visual de dano
    lastDamageTime: 0,
    lastDamageAmount: 0,

    lastPlayerShotAt: 0,
    playerStealthed: false,
    playerStealthRadius: 0,
    setPlayerStealth: (stealthed, radius = 0) => {
      const state = get();
      if (state.playerStealthed !== stealthed || state.playerStealthRadius !== radius) {
        set({ playerStealthed: stealthed, playerStealthRadius: radius });
      }
    },

    // [BESTIÁRIO] Estado local da Fênix e do sopro do Dragão
    playerRebirthUsed: false,
    playerRebirthInvulnUntil: 0,
    lastRebirthAt: 0,
    enemyBurns: {},

    // Incendeia um inimigo (impacto do sopro). Reaplicar renova a duração
    // e mantém o tick mais forte — mesma regra do servidor Colyseus.
    igniteEnemy: (id, tickDamage) => {
      const now = Date.now();
      set((state) => {
        const current = state.enemyBurns[id];
        return {
          enemyBurns: {
            ...state.enemyBurns,
            [id]: {
              until: now + BURN_DURATION_MS,
              nextTickAt: current ? Math.min(current.nextTickAt, now + BURN_TICK_MS) : now + BURN_TICK_MS,
              tickDamage: Math.max(tickDamage, current?.tickDamage ?? 0),
            },
          },
        };
      });
    },

    // Tick dos incêndios ativos (chamado por frame pelo TankSpecialsController).
    tickEnemyBurns: () => {
      const state = get();
      const ids = Object.keys(state.enemyBurns);
      if (ids.length === 0) return;
      const now = Date.now();
      const remaining: Record<string, { until: number; nextTickAt: number; tickDamage: number }> = {};
      let changed = false;

      for (const id of ids) {
        const burn = state.enemyBurns[id];
        const enemy = state.enemyTanks.find((e) => e.id === id);
        if (!enemy || !enemy.isAlive || now >= burn.until) {
          changed = true;
          continue;
        }
        if (now >= burn.nextTickAt) {
          changed = true;
          get().damageEnemy(id, burn.tickDamage);
          remaining[id] = { ...burn, nextTickAt: burn.nextTickAt + BURN_TICK_MS };
        } else {
          remaining[id] = burn;
        }
      }

      if (changed) set({ enemyBurns: remaining });
    },
    
    setPhase: (phase) => {
      if (phase === "menu") {
        // [FIX] Limpar estado de IA do GenAI ao voltar ao menu
        resetAIState();
        brLiveZone.active = false;
        // A arena morre com a partida: sem isto o menu (e a partida seguinte,
        // até ela montar a própria arena) herdaria as poças da anterior —
        // invisíveis, porque o render é de outro modo, e ainda assim cobrando.
        clearArenaSurface();
        resetTankSurfaces();
        set({
          phase, 
          moveDirection: new THREE.Vector2(0, 0), 
          isFiring: false,
          aimDirection: new THREE.Vector2(0, -1),
          isMultiplayer: false,
          gameMode: "roguelike" as GameMode,
          brElectricWallActive: false,
          brPlayerRank: null,
          brIsSpectating: false,
          brSpectatingTargetId: null,
          brEliminationFeed: [],
          multiplayerIsSpectating: false,
          multiplayerSpectatingTargetId: null,
          isAiVsAi: false,
        });
      } else {
        set({ phase });
      }
    },
    
    setGameMode: (mode: GameMode) => {
      set({ gameMode: mode });
    },
    
    startGame: () => {
      const { selectedTerrain, isMultiplayer, gameMode } = get();
      const terrainToUse = isMultiplayer && selectedTerrain ? selectedTerrain : undefined;
      set({
        playerTank: null, items: [], heldItem: null, activeEffect: null, frozenEnemies: new Map(),
        // [BESTIÁRIO] Nova partida: Fênix recupera o renascimento, fogos apagam
        playerRebirthUsed: false, playerRebirthInvulnUntil: 0, lastRebirthAt: 0, enemyBurns: {},
      });
      
      if (gameMode === "battleRoyale") {
        get().startBattleRoyale();
        return;
      }

      // Uma corrida nova sorteia sua própria semente. Antes a arena vinha de
      // `wave * 1337`: a onda 12 era literalmente a mesma arena em toda
      // partida, para todo jogador.
      if (isInfiniteMode({ gameMode, isMultiplayer })) {
        useInfiniteRun.getState().beginRun();
      }

      get().initializeWave(1, terrainToUse);
      set({
        phase: "playing",
        score: 0,
        wave: 1,
        killsThisWave: 0,
        pausedBy: null,
        moveDirection: new THREE.Vector2(0, 0),
        isFiring: false,
        aimDirection: new THREE.Vector2(0, -1),
      });
    },
    
    startBattleRoyale: (botCount?: number, customArenaSize?: number, terrainTheme?: string, blockDensity?: number) => {
      // O terreno vive fora do React e não se apaga sozinho: sem esta limpeza,
      // uma poça de lava da partida anterior continuaria ativa aqui — e um
      // estado de fogo, grudado no tanque que acabou de entrar.
      clearArenaSurface();
      resetTankSurfaces();
      const progress = useProgress.getState();
      const difficultyState = useDifficulty.getState();
      const difficultyConfig = DIFFICULTY_CONFIGS[difficultyState.difficulty];
      
      const selectedTankId = progress.selectedTankId;
      const tankDef = progress.tanks.find(t => t.id === selectedTankId) || progress.tanks[0];
      
      // Usar skin e efeitos selecionados pelo jogador
      const selectedSkin = progress.getSelectedSkin();
      const selectedEffect = progress.getSelectedEffect();
      
      const playerSpawnX = 0;
      const playerSpawnZ = 0;
      
      const playerTank: TankData = {
        id: "player",
        position: new THREE.Vector3(playerSpawnX, 0, playerSpawnZ),
        rotation: 0,
        health: tankDef.health,
        maxHealth: tankDef.health,
        color: tankDef.color,
        isPlayer: true,
        isAlive: true,
        name: "Você",
        lastFireTime: 0,
        speed: tankDef.speed,
        damage: tankDef.damage,
        shotCount: tankDef.bulletCount ?? 1,
        size: tankDef.scale ?? 1,
        fireRate: tankDef.fireRate ?? 1,
        skinId: selectedSkin?.id || null,
        effectId: selectedEffect?.id || null,
      };
      
      // Clamps de segurança: o slider da UI já limita, mas o cap canônico é
      // aplicado aqui para qualquer chamador (inclusive valores persistidos).
      const bots = Math.max(1, Math.min(botCount || get().brBotCount, BR_CONFIG.MAX_BOT_COUNT));
      const arenaSize = Math.min(
        BR_CONFIG.MAX_ARENA_SIZE,
        Math.max(BR_CONFIG.MIN_ARENA_SIZE * 4, customArenaSize || BR_CONFIG.INITIAL_ARENA_SIZE),
      );
      const enemyTanks: TankData[] = [];

      // ---- Terreno ----
      // Mesmo plano da Batalha Infinita, outro perfil: a arena do BR é três
      // vezes maior, então a receita é repetida em vez de virar uma mancha
      // gigante, e o peso de arena limpa é baixo — o mapa grande é justamente
      // onde o terreno tem espaço para mudar a rota de quem foge da zona.
      const brTerrain = planArenaTerrain({
        mode: "battleRoyale",
        seed: Math.floor(Math.random() * 0x7fffffff),
        arenaSize,
        // O centro já é zona franca de spawn; os bots nascem espalhados e são
        // empurrados para fora do perigo depois de sorteados.
        safeSpots: [{ x: 0, z: 0, radius: 10 }],
      });
      const brHazards = brTerrain.zones;

      // Gerar obstáculos estilo Bomberman (zona segura central de raio 10
      // para o spawn do jogador)
      const boundary = arenaSize / 2 - 4;
      const rawBrObstacles = generateGridObstacles({
        arenaSize,
        gridSpacing: 6,
        boundaryMargin: 4,
        idPrefix: "br-obs",
        isInSafeZone: (x, z) => Math.sqrt(x * x + z * z) < 10,
        destructibleDensity: blockDensity ?? 0.4,
        destructibleHealth: 3,
        blockFootprint: 2.5,
        indestructibleHeight: 3,
        destructibleHeight: 2,
      });

      // Bloco dentro de um lago ou de uma poça de lava pareceria bug de
      // geração; ali o perigo tem de ser o obstáculo.
      const obstacles = brHazards.length > 0
        ? rawBrObstacles.filter((obs) => !isInsideHazard(obs.position.x, obs.position.z, brHazards, 0.8))
        : rawBrObstacles;

      // Índice espacial grosseiro dos obstáculos: o teste de spawn era uma
      // varredura linear de TODOS os obstáculos × 50 tentativas × N bots —
      // em arenas grandes isso travava a thread principal por segundos.
      const OBS_CELL = 6;
      const obstacleCells = new Set<string>();
      obstacles.forEach((obs) => {
        obstacleCells.add(`${Math.round(obs.position.x / OBS_CELL)},${Math.round(obs.position.z / OBS_CELL)}`);
      });
      const collidesWithObstacles = (x: number, z: number) => {
        const cx = Math.round(x / OBS_CELL);
        const cz = Math.round(z / OBS_CELL);
        for (let dx = -1; dx <= 1; dx++) {
          for (let dz = -1; dz <= 1; dz++) {
            if (obstacleCells.has(`${cx + dx},${cz + dz}`)) return true;
          }
        }
        return false;
      };
      
      const botNames = ['Shadow', 'Thunder', 'Blaze', 'Frost', 'Storm', 'Viper', 'Phoenix', 'Titan',
        'Ghost', 'Reaper', 'Hunter', 'Sniper', 'Tank', 'Destroyer', 'Warrior', 'Knight',
        'Dragon', 'Wolf', 'Eagle', 'Panther', 'Tiger', 'Lion', 'Bear', 'Hawk'];
      const botColors = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', 
        '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#6366f1'];
      
      const baseHealth = 100;
      const baseSpeed = 8;
      const baseDamage = 18;
      const baseAILevel = 0.7;
      
      for (let i = 0; i < bots; i++) {
        // Tentar encontrar posição válida (sem colisão com obstáculos)
        let x = 0, z = 0;
        let attempts = 0;
        const maxAttempts = 50;
        
        do {
          const angle = (i / bots) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
          const distance = (arenaSize / 2 - 16) * (0.3 + Math.random() * 0.7);
          x = Math.cos(angle) * distance;
          z = Math.sin(angle) * distance;
          attempts++;
        } while (collidesWithObstacles(x, z) && attempts < maxAttempts);
        
        // Fallback: se não encontrou posição, usar borda da arena
        if (attempts >= maxAttempts) {
          const angle = (i / bots) * Math.PI * 2;
          x = Math.cos(angle) * (boundary - 5);
          z = Math.sin(angle) * (boundary - 5);
        }
        
        const adjustedHealth = Math.round(baseHealth * difficultyConfig.healthMultiplier);
        const adjustedSpeed = baseSpeed * difficultyConfig.speedMultiplier;
        const adjustedDamage = Math.round(baseDamage * difficultyConfig.damageMultiplier);
        const adjustedAILevel = Math.min(1, baseAILevel * difficultyConfig.aiLevelMultiplier);
        
        enemyTanks.push({
          id: `bot_${i}`,
          position: new THREE.Vector3(x, 0, z),
          rotation: Math.random() * Math.PI * 2,
          health: adjustedHealth,
          maxHealth: adjustedHealth,
          color: botColors[i % botColors.length],
          isPlayer: false,
          isAlive: true,
          name: botNames[i % botNames.length] + Math.floor(Math.random() * 99),
          lastFireTime: 0,
          speed: adjustedSpeed,
          damage: adjustedDamage,
          aiLevel: adjustedAILevel,
          activeEffect: null,
          shieldHits: 0,
        });
      }
      
      // Bot que nasce dentro da lava morre sozinho antes de qualquer tiro: a
      // partida começaria com eliminações que ninguém fez.
      if (brHazards.length > 0) {
        for (const bot of enemyTanks) {
          const safe = pushOutOfHazards(bot.position.x, bot.position.z, brHazards, arenaSize);
          if (safe) bot.position.set(safe.x, bot.position.y, safe.z);
        }
      }

      setArenaSurface(brHazards, arenaSize);

      // Canal vivo da zona: começa no tamanho cheio da arena
      brLiveZone.current = arenaSize;
      brLiveZone.active = true;

      // Recompensa de vitória anterior: item pendente vira equipamento
      // inicial desta partida (a "Caixa Mistério" sorteia um item real).
      const pendingItemId = useProgress.getState().consumePendingBRItem?.() ?? null;
      let startingItem: ItemType | null = null;
      if (pendingItemId) {
        const resolved = pendingItemId === "mystery"
          ? (["shield", "intense", "freeze", "heal"] as ItemType[])[Math.floor(Math.random() * 4)]
          : (pendingItemId as ItemType);
        if (ITEM_CONFIG[resolved]) startingItem = resolved;
      }

      set({
        gameMode: "battleRoyale" as GameMode,
        phase: "playing",
        playerTank,
        enemyTanks,
        bullets: [],
        obstacles,
        items: [],
        explosions: [],
        score: 0,
        wave: 1,
        killsThisWave: 0,
        arenaSize: arenaSize,
        arenaShape: "square" as ArenaShape,
        terrain: terrainTheme && TERRAIN_THEMES[terrainTheme as keyof typeof TERRAIN_THEMES] 
          ? TERRAIN_THEMES[terrainTheme as keyof typeof TERRAIN_THEMES] 
          : TERRAIN_THEMES.cyber,
        brCurrentArenaSize: arenaSize,
        brTargetArenaSize: arenaSize,
        brIsShrinking: false,
        brElectricWallActive: true,
        brAliveCount: enemyTanks.length + 1,
        brPlayerRank: null,
        brBotCount: bots,
        // Partida nova começa sem resquício da anterior. Importa porque o
        // "jogar de novo" da tela de fim de partida entra aqui direto, sem
        // passar pelo menu (que é quem limpava isso até agora).
        brIsSpectating: false,
        brSpectatingTargetId: null,
        brEliminationFeed: [],
        brLastConfig: { botCount: bots, arenaSize, terrainTheme, blockDensity },
        moveDirection: new THREE.Vector2(0, 0),
        isFiring: false,
        aimDirection: new THREE.Vector2(0, -1),
        heldItem: startingItem,
        activeEffect: null,
        frozenEnemies: new Map(),
        // [BESTIÁRIO] Partida nova: Fênix recupera o renascimento, fogos apagam
        playerRebirthUsed: false,
        playerRebirthInvulnUntil: 0,
        lastRebirthAt: 0,
        enemyBurns: {},
      });
    },

    restartBattleRoyale: () => {
      const cfg = get().brLastConfig;
      get().startBattleRoyale(
        cfg?.botCount,
        cfg?.arenaSize,
        cfg?.terrainTheme,
        cfg?.blockDensity,
      );
    },

    startGenAI: (config: GenAIConfigType) => {
      // O terreno vive fora do React e não se apaga sozinho: sem esta limpeza,
      // uma poça de lava da partida anterior continuaria ativa aqui — e um
      // estado de fogo, grudado no tanque que acabou de entrar.
      clearArenaSurface();
      resetTankSurfaces();
      // [FIX] Limpar estado residual de sessões anteriores
      resetAIState();
      // [BESTIÁRIO] Nova partida: Fênix recupera o renascimento, fogos apagam
      set({ playerRebirthUsed: false, playerRebirthInvulnUntil: 0, lastRebirthAt: 0, enemyBurns: {} });

      const progress = useProgress.getState();
      const selectedTankId = progress.selectedTankId;
      const tankDef = progress.tanks.find(t => t.id === selectedTankId) || progress.tanks[0];
      const selectedSkin = progress.getSelectedSkin();
      const selectedEffect = progress.getSelectedEffect();
      
      const arenaSize = config.arenaSize || 40;
      const boundary = arenaSize / 2 - 2;
      
      const isPlayerMode = config.mode === "player-vs-ai";
      const aiColors = ["#ef4444", "#3b82f6", "#22c55e", "#f97316"];
      
      // Mode Player vs AI: player controls tank, AI is enemy
      // Mode AI vs AI: both are AIs, player spectates
      
      let playerTank: TankData | null = null;
      const enemyTanks: TankData[] = [];
      
      if (isPlayerMode) {
        // Player tank with selected tank stats/cosmetics
        playerTank = {
          id: "player",
          position: new THREE.Vector3(-boundary + 5, 0, 0),
          rotation: 0,
          health: tankDef.health,
          maxHealth: tankDef.health,
          color: tankDef.color,
          isPlayer: true,
          isAlive: true,
          name: "Você",
          lastFireTime: 0,
          speed: tankDef.speed,
          damage: tankDef.damage,
          shotCount: tankDef.bulletCount ?? 1,
          size: tankDef.scale ?? 1,
          fireRate: tankDef.fireRate ?? 1,
          skinId: selectedSkin?.id || null,
          effectId: selectedEffect?.id || null,
        };
        
        // AI opponent
        enemyTanks.push({
          id: "ai_1",
          position: new THREE.Vector3(boundary - 5, 0, 0),
          rotation: Math.PI,
          health: 120,
          maxHealth: 120,
          color: aiColors[0],
          isPlayer: false,
          isAlive: true,
          name: config.aiProvider1 === "openai" ? "GPT-4" : "Gemini",
          lastFireTime: 0,
          speed: 10,
          damage: 20,
          aiLevel: config.personality1 === "aggressive" ? 1.0 : 
                   config.personality1 === "defensive" ? 0.6 : 0.8,
          activeEffect: null,
          shieldHits: 0,
        });
      } else {
        // AI vs AI mode: both tanks are AI controlled
        // First AI (left side)
        const ai1: TankData = {
          id: "ai_1",
          position: new THREE.Vector3(-boundary + 5, 0, 0),
          rotation: 0,
          health: 120,
          maxHealth: 120,
          color: aiColors[0],
          isPlayer: false,
          isAlive: true,
          name: config.aiProvider1 === "openai" ? "GPT-4 🔵" : "Gemini 🔵",
          lastFireTime: 0,
          speed: 10,
          damage: 20,
          aiLevel: config.personality1 === "aggressive" ? 1.0 : 
                   config.personality1 === "defensive" ? 0.6 : 0.8,
          activeEffect: null,
          shieldHits: 0,
        };
        
        // Second AI (right side)
        const ai2: TankData = {
          id: "ai_2",
          position: new THREE.Vector3(boundary - 5, 0, 0),
          rotation: Math.PI,
          health: 120,
          maxHealth: 120,
          color: aiColors[1],
          isPlayer: false,
          isAlive: true,
          name: (config.aiProvider2 === "openai" ? "GPT-4 🔴" : "Gemini 🔴"),
          lastFireTime: 0,
          speed: 10,
          damage: 20,
          aiLevel: config.personality2 === "aggressive" ? 1.0 : 
                   config.personality2 === "defensive" ? 0.6 : 0.8,
          activeEffect: null,
          shieldHits: 0,
        };
        
        // In AI-vs-AI, set ai1 as "playerTank" for camera tracking (but isPlayer=false)
        playerTank = ai1;
        enemyTanks.push(ai2);
      }
      
      // ---- Terreno ----
      // Duelo em arena pequena com dois spawns em lados opostos: as duas pontas
      // entram como zonas francas para ninguém começar dentro de um lago.
      const genaiTerrain = planArenaTerrain({
        mode: "genai",
        seed: Math.floor(Math.random() * 0x7fffffff),
        arenaSize,
        safeSpots: [
          { x: -boundary + 5, z: 0, radius: 7 },
          { x: boundary - 5, z: 0, radius: 7 },
        ],
      });

      // Obstáculos simples para arena GenAI: só pilares indestrutíveis,
      // com quadrado central livre para os spawns dos tanques.
      const rawGenaiObstacles = generateGridObstacles({
        arenaSize,
        gridSpacing: 8,
        // Mesmo recuo de borda do código original: boundary(-2) - 4 → margem 6.
        boundaryMargin: 6,
        idPrefix: "genai-obs",
        isInSafeZone: (x, z) => Math.abs(x) < 8 && Math.abs(z) < 8,
        destructibleDensity: 0,
        destructibleHealth: 1,
        blockFootprint: 2.5,
        indestructibleHeight: 3,
        destructibleHeight: 2,
      });

      const obstacles = genaiTerrain.zones.length > 0
        ? rawGenaiObstacles.filter(
            (obs) => !isInsideHazard(obs.position.x, obs.position.z, genaiTerrain.zones, 0.8),
          )
        : rawGenaiObstacles;

      setArenaSurface(genaiTerrain.zones, arenaSize);

      const terrainKey = config.terrain as keyof typeof TERRAIN_THEMES;
      const terrain = TERRAIN_THEMES[terrainKey] || TERRAIN_THEMES.industrial;
      
      // Gerar ID único para esta sessão de partida
      const matchSessionId = `genai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      set({
        gameMode: "genai" as GameMode,
        phase: "playing",
        playerTank,
        enemyTanks,
        bullets: [],
        obstacles,
        items: [],
        score: 0,
        wave: 1,
        killsThisWave: 0,
        arenaSize,
        arenaShape: "square" as ArenaShape,
        terrain,
        moveDirection: new THREE.Vector2(0, 0),
        isFiring: false,
        aimDirection: new THREE.Vector2(0, -1),
        heldItem: null,
        activeEffect: null,
        frozenEnemies: new Map(),
        isAiVsAi: !isPlayerMode,
        genAIProvider1: config.aiProvider1,
        genAIProvider2: config.aiProvider2 || null,
        genAIPersonality: config.personality1,
        genAIPersonality2: config.personality2 || null,
        genAIThought: "...",
        genAIThought2: "...",
        genAIProviderSwitched1: false,
        genAIProviderSwitched2: false,
        genAIMatchSessionId: matchSessionId,
        // Inicializar rastreamento de padrões do jogador
        genAIPlayerPatterns: {
          totalMovementDistance: 0,
          movementSamples: 0,
          lastPosition: null,
          totalShots: 0,
          matchStartTime: Date.now(),
          nearMisses: 0,
          totalDodgeOpportunities: 0,
          attackDistances: [],
          timeNearEnemy: 0,
          timeFarFromEnemy: 0,
          timeMediumRange: 0,
        },
      });

      // Registrar jogador no mapa ao vivo do admin
      const playerId = localStorage.getItem("rocketanks_player_id") || `player_${Date.now()}`;
      if (!localStorage.getItem("rocketanks_player_id")) {
        localStorage.setItem("rocketanks_player_id", playerId);
      }
      apiFetch("/api/genai/player-connect", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ playerId, playerName: "Player" }),
      }).catch(() => {});
    },
    
    updateBRArena: () => {
      const { brCurrentArenaSize, brTargetArenaSize, brIsShrinking } = get();
      if (!brIsShrinking) return;

      // [PERF] O valor vivo anda todo frame no canal transiente (parede,
      // câmera e clamp de movimento leem direto de lá); o store só recebe
      // commits em passos de ~1 unidade — antes eram 60 re-renders/s da
      // cena inteira durante cada fase de encolhimento.
      const live = brLiveZone.active ? brLiveZone.current : brCurrentArenaSize;
      const newSize = Math.max(brTargetArenaSize, live - BR_CONFIG.SHRINK_SPEED);
      brLiveZone.current = newSize;
      brLiveZone.active = true;

      const done = newSize <= brTargetArenaSize;
      if (done || brCurrentArenaSize - newSize >= 1) {
        set({
          brCurrentArenaSize: newSize,
          brIsShrinking: !done,
          arenaSize: newSize,
        });
      }
    },
    
    shrinkBRArena: (shrinkDelta: number) => {
      const { brCurrentArenaSize } = get();
      const newTarget = Math.max(BR_CONFIG.MIN_ARENA_SIZE, brCurrentArenaSize - shrinkDelta);
      
      set({
        brTargetArenaSize: newTarget,
        brIsShrinking: true,
      });
    },
    
    setBRPlayerRank: (rank: number | null) => {
      set({ brPlayerRank: rank });
    },
    
    updateBRAliveCount: () => {
      const { playerTank, enemyTanks } = get();
      const aliveEnemies = enemyTanks.filter(e => e.isAlive).length;
      const playerAlive = playerTank?.isAlive ? 1 : 0;
      set({ brAliveCount: aliveEnemies + playerAlive });
    },
    
    startBRSpectating: () => {
      const { enemyTanks } = get();
      const aliveBots = enemyTanks.filter(e => e.isAlive);
      if (aliveBots.length === 0) {
        set({ brIsSpectating: false, brSpectatingTargetId: null });
        return;
      }
      // Focar em quem está "ganhando" - maior HP significa que sobreviveu mais tempo
      const sortedByHealth = [...aliveBots].sort((a, b) => b.health - a.health);
      const targetId = sortedByHealth[0].id;
      set({ brIsSpectating: true, brSpectatingTargetId: targetId });
    },
    
    stopBRSpectating: () => {
      set({ 
        brIsSpectating: false, 
        brSpectatingTargetId: null,
        brEliminationFeed: [],
      });
      get().setPhase("menu");
    },
    
    nextBRSpectateTarget: () => {
      const { enemyTanks, brSpectatingTargetId } = get();
      const aliveBots = enemyTanks.filter(e => e.isAlive);
      if (aliveBots.length === 0) {
        set({ brIsSpectating: false, brSpectatingTargetId: null });
        return;
      }
      
      // Ordenar por HP para facilitar navegação entre os melhores
      const sortedByHealth = [...aliveBots].sort((a, b) => b.health - a.health);
      const currentIndex = sortedByHealth.findIndex(b => b.id === brSpectatingTargetId);
      const nextIndex = (currentIndex + 1) % sortedByHealth.length;
      set({ brSpectatingTargetId: sortedByHealth[nextIndex].id });
    },
    
    // Atualiza o alvo do espectador se o atual morreu
    updateBRSpectateTarget: () => {
      const { brIsSpectating, brSpectatingTargetId, enemyTanks } = get();
      if (!brIsSpectating) return;
      
      const currentTarget = enemyTanks.find(e => e.id === brSpectatingTargetId);
      if (currentTarget && currentTarget.isAlive) return; // Alvo ainda vivo
      
      // Alvo morreu, encontrar próximo melhor
      const aliveBots = enemyTanks.filter(e => e.isAlive);
      if (aliveBots.length === 0) {
        set({ brIsSpectating: false, brSpectatingTargetId: null });
        return;
      }
      
      const sortedByHealth = [...aliveBots].sort((a, b) => b.health - a.health);
      set({ brSpectatingTargetId: sortedByHealth[0].id });
    },
    
    addBREliminationFeed: (eliminatedName: string, killerName: string) => {
      const { brEliminationFeed } = get();
      const newFeed = [
        ...brEliminationFeed,
        { eliminatedName, killerName, timestamp: Date.now() }
      ].slice(-10);
      set({ brEliminationFeed: newFeed });
    },
    
    // [MULTIPLAYER-SPECTATOR] Ativar espectador multiplayer
    startMultiplayerSpectating: () => {
      const { remotePlayersMap } = get();
      const alivePlayers = Array.from(remotePlayersMap.values()).filter(p => p.isAlive);
      if (alivePlayers.length === 0) {
        set({ multiplayerIsSpectating: false, multiplayerSpectatingTargetId: null });
        return;
      }
      // Focar em quem está "ganhando" - maior HP
      const sortedByHealth = [...alivePlayers].sort((a, b) => b.health - a.health);
      const targetId = sortedByHealth[0].sessionId;
      set({ multiplayerIsSpectating: true, multiplayerSpectatingTargetId: targetId });
    },
    
    stopMultiplayerSpectating: () => {
      set({ 
        multiplayerIsSpectating: false, 
        multiplayerSpectatingTargetId: null,
      });
    },
    
    setMultiplayerSpectateTarget: (targetId: string) => {
      const { remotePlayersMap } = get();
      const target = remotePlayersMap.get(targetId);
      if (target && target.isAlive) {
        set({ multiplayerSpectatingTargetId: targetId });
      }
    },

    nextMultiplayerSpectateTarget: () => {
      const { remotePlayersMap, multiplayerSpectatingTargetId } = get();
      const alivePlayers = Array.from(remotePlayersMap.values()).filter(p => p.isAlive);
      if (alivePlayers.length === 0) {
        set({ multiplayerIsSpectating: false, multiplayerSpectatingTargetId: null });
        return;
      }
      
      const sortedByHealth = [...alivePlayers].sort((a, b) => b.health - a.health);
      const currentIndex = sortedByHealth.findIndex(p => p.sessionId === multiplayerSpectatingTargetId);
      const nextIndex = (currentIndex + 1) % sortedByHealth.length;
      set({ multiplayerSpectatingTargetId: sortedByHealth[nextIndex].sessionId });
    },
    
    updateMultiplayerSpectateTarget: () => {
      const { multiplayerIsSpectating, multiplayerSpectatingTargetId, remotePlayersMap } = get();
      if (!multiplayerIsSpectating) return;
      
      const currentTarget = remotePlayersMap.get(multiplayerSpectatingTargetId || "");
      if (currentTarget && currentTarget.isAlive) return; // Alvo ainda vivo
      
      // Alvo morreu, encontrar próximo melhor
      const alivePlayers = Array.from(remotePlayersMap.values()).filter(p => p.isAlive);
      if (alivePlayers.length === 0) {
        set({ multiplayerIsSpectating: false, multiplayerSpectatingTargetId: null });
        return;
      }
      
      const sortedByHealth = [...alivePlayers].sort((a, b) => b.health - a.health);
      set({ multiplayerSpectatingTargetId: sortedByHealth[0].sessionId });
    },
    
    endGame: (won) => {
      const state = get();
      const infinite = isInfiniteMode(state);

      if (won) {
        if (infinite) {
          const run = useInfiniteRun.getState();
          const elapsed = run.waveStartedAt > 0 ? (Date.now() - run.waveStartedAt) / 1000 : 0;
          // Fecha o contrato antes de abrir o resumo: contratos de contagem que
          // não bateram a meta precisam falhar agora, não na onda seguinte.
          const tank = state.playerTank;
          run.pushContractEvent({
            type: "waveCleared",
            elapsedSeconds: elapsed,
            healthPercent: tank ? (tank.health / tank.maxHealth) * 100 : 0,
          });
          run.registerWaveReached(state.wave);
        }
        set({ phase: "waveComplete" });
      } else {
        if (infinite) {
          clearArenaSurface();
          resetTankSurfaces();
          useInfiniteRun.getState().endRun(state.wave, state.score);
        }
        set({ phase: "gameOver" });
      }
    },
    
    restartGame: () => {
      const state = get();

      // GenAI mode: re-initialize via startGenAI to avoid spawning standard wave enemies
      if (state.gameMode === "genai") {
        const config: GenAIConfigType = {
          mode: state.isAiVsAi ? "ai-vs-ai" : "player-vs-ai",
          aiProvider1: state.genAIProvider1 || "openai",
          aiProvider2: state.isAiVsAi ? (state.genAIProvider2 || "openai") : undefined,
          personality1: state.genAIPersonality || "strategic",
          personality2: state.isAiVsAi ? (state.genAIPersonality2 || "strategic") : undefined,
          terrain: state.terrain?.theme || "industrial",
          arenaSize: state.arenaSize,
        };
        get().startGenAI(config);
        return;
      }

      const { selectedTerrain, isMultiplayer } = state;
      const terrainToUse = isMultiplayer && selectedTerrain ? selectedTerrain : undefined;
      set({
        playerTank: null, items: [], heldItem: null, activeEffect: null, frozenEnemies: new Map(),
        // [BESTIÁRIO] Nova partida: Fênix recupera o renascimento, fogos apagam
        playerRebirthUsed: false, playerRebirthInvulnUntil: 0, lastRebirthAt: 0, enemyBurns: {},
      });

      // Recomeçar é uma corrida nova: semente, cartas e contratos zerados.
      if (isInfiniteMode(state)) {
        useInfiniteRun.getState().beginRun();
      }

      get().initializeWave(1, terrainToUse);
      set({
        phase: "playing",
        score: 0,
        wave: 1,
        killsThisWave: 0,
        pausedBy: null,
        moveDirection: new THREE.Vector2(0, 0),
        isFiring: false,
        aimDirection: new THREE.Vector2(0, -1),
      });
    },
    
    togglePause: (playerId?: string) => {
      const { phase } = get();
      if (phase === "playing" || phase === "storyPlaying") {
        set({ phase: "paused", pausedBy: playerId || "player", previousPhase: phase, pausedAt: Date.now() });
      } else if (phase === "paused") {
        const { previousPhase, pausedAt, isMultiplayer } = get();
        // Timestamps de gameplay usam wall-clock (Date.now); sem compensação,
        // efeitos expiravam e balas sumiam "durante" a pausa.
        if (pausedAt !== null && !isMultiplayer) {
          get().compensateTimeJump(Date.now() - pausedAt);
        }
        set({ phase: previousPhase || "playing", pausedBy: null, previousPhase: null, pausedAt: null });
      }
    },

    setPaused: (paused: boolean, playerId?: string) => {
      const { phase } = get();
      if (paused && phase !== "paused") {
        set({ phase: "paused", pausedBy: playerId || "player", previousPhase: phase, pausedAt: Date.now() });
      } else if (!paused && phase === "paused") {
        const { previousPhase, pausedAt, isMultiplayer } = get();
        if (pausedAt !== null && !isMultiplayer) {
          get().compensateTimeJump(Date.now() - pausedAt);
        }
        set({ phase: previousPhase || "playing", pausedBy: null, previousPhase: null, pausedAt: null });
      }
    },

    // Desloca todos os timestamps de gameplay (wall-clock) para frente após
    // um "salto de tempo" em que o jogo não rodou: pausa ou aba em background.
    // Sem isso, ao retomar, efeitos ativos já teriam expirado, balas sumiriam
    // e cooldowns estariam zerados.
    compensateTimeJump: (deltaMs: number) => {
      if (deltaMs <= 0) return;
      set((state) => ({
        bullets: state.bullets.map((b) => ({ ...b, createdAt: b.createdAt + deltaMs })),
        items: state.items.map((i) => ({ ...i, createdAt: i.createdAt + deltaMs })),
        explosions: state.explosions.map((e) => ({ ...e, createdAt: e.createdAt + deltaMs })),
        bossMines: state.bossMines.map((m) => ({ ...m, createdAt: m.createdAt + deltaMs })),
        activeEffect: state.activeEffect
          ? { ...state.activeEffect, expiresAt: state.activeEffect.expiresAt + deltaMs }
          : null,
        frozenEnemies: new Map(
          Array.from(state.frozenEnemies.entries(), ([id, until]) => [id, until + deltaMs] as [string, number])
        ),
        playerTank: state.playerTank
          ? { ...state.playerTank, lastFireTime: state.playerTank.lastFireTime + deltaMs }
          : null,
        enemyTanks: state.enemyTanks.map((e) => ({
          ...e,
          lastFireTime: e.lastFireTime + deltaMs,
          activeEffect: e.activeEffect
            ? { ...e.activeEffect, expiresAt: e.activeEffect.expiresAt + deltaMs }
            : e.activeEffect,
        })),
        astraActiveUntil: state.astraActiveUntil > 0 ? state.astraActiveUntil + deltaMs : state.astraActiveUntil,
        astraCooldownUntil: state.astraCooldownUntil > 0 ? state.astraCooldownUntil + deltaMs : state.astraCooldownUntil,
        chargeStartTime: state.isCharging ? state.chargeStartTime + deltaMs : state.chargeStartTime,
      }));
    },
    
    setMultiplayer: (isMultiplayer: boolean) => {
      set({ isMultiplayer });
    },
    
    nextWave: () => {
      const { wave, playerTank, isMultiplayer, selectedTerrain, storyNoHealing } = get();
      const nextWaveNum = wave + 1;
      
      // Track wave reached for missions
      useDailyMissions.getState().updateProgress('reach_wave', nextWaveNum);
      
      // Track no-damage wave if player is at max health
      if (playerTank && playerTank.health === playerTank.maxHealth) {
        useDailyMissions.getState().updateProgress('no_damage_wave', 1);
      }
      
      const maxHealth = playerTank?.maxHealth || 100;
      const currentHealth = playerTank?.health || maxHealth;
      // Cura base entre ondas, mais o que as cartas de reparo adicionarem —
      // vida vira recurso que o draft pode comprar, em vez de constante fixa.
      const infinite = isInfiniteMode(get());
      const healBase = 30 + (infinite ? useInfiniteRun.getState().modifiers.waveHealBonus : 0);
      // No healing between waves if storyNoHealing is active (Chapter 4)
      const healAmount = storyNoHealing ? 0 : Math.min(healBase, maxHealth - currentHealth);

      const terrainToUse = isMultiplayer && selectedTerrain ? selectedTerrain : undefined;
      get().initializeWave(nextWaveNum, terrainToUse);
      
      set((state) => ({
        phase: "playing",
        wave: nextWaveNum,
        killsThisWave: 0,
        playerTank: state.playerTank ? {
          ...state.playerTank,
          health: Math.min(maxHealth, currentHealth + healAmount),
        } : null,
      }));
    },
    
    setMoveDirection: (dir) => set({ moveDirection: dir }),
    setIsFiring: (firing) => set({ isFiring: firing }),
    setAimDirection: (dir) => set({ aimDirection: dir }),
    
    startCharging: () => set({ isCharging: true, chargeStartTime: Date.now(), chargeLevel: 0 }),
    
    updateChargeLevel: () => {
      const { isCharging, chargeStartTime } = get();
      if (!isCharging) return;
      const elapsed = Date.now() - chargeStartTime;
      const level = Math.min(elapsed / 2000, 1);
      set({ chargeLevel: level });
    },
    
    releaseCharge: () => {
      const { chargeLevel } = get();
      set({ isCharging: false, chargeStartTime: 0, chargeLevel: 0 });
      return chargeLevel;
    },

    cancelCharge: () => {
      set({ isCharging: false, chargeStartTime: 0, chargeLevel: 0 });
    },
    
    updatePlayerPosition: (pos, rotation) => {
      // Hot path (chamado a cada frame): muta o Vector3 existente em vez de
      // criar novo objeto/clone via set(). Nenhum consumidor lê posição de
      // forma reativa (todos usam getState() em loops de frame); mudanças de
      // vida/morte continuam imutáveis e disparam re-render normalmente.
      const { playerTank } = get();
      if (!playerTank) return;

      // Se o store acabou de receber um spawn/teleporte (nova onda, sala de
      // dungeon) que o objeto 3D ainda não aplicou, ignora este write-back —
      // ele viria da posição antiga e destruiria o spawn antes do snap.
      const dx = playerTank.position.x - pos.x;
      const dz = playerTank.position.z - pos.z;
      if (dx * dx + dz * dz > SPAWN_TELEPORT_DISTANCE_SQ) return;

      playerTank.position.copy(pos);
      playerTank.rotation = rotation;
    },
    
    updatePlayerHealth: (health) => {
      const { playerTank, gameMode, enemyTanks, isMultiplayer } = get();
      if (playerTank) {
        // Poder de linhagem "shield": reduz TODO dano recebido nos modos
        // locais. Aplicado aqui (única porta de entrada de dano do jogador)
        // para valer igualmente para balas, minas, EMP, airstrike e bosses.
        // Em multiplayer o servidor Colyseus já aplica a redução
        // (SpecialsSystem) — sem redução local para não descontar em dobro.
        const rawDamage = playerTank.health - health;
        if (rawDamage > 0 && !isMultiplayer) {
          // [BESTIÁRIO] Invulnerabilidade breve pós-renascimento da Fênix
          if (Date.now() < get().playerRebirthInvulnUntil) return;
          const special = getPlayerTankSpecial();
          if (special?.type === "shield") {
            health = playerTank.health - applyShieldReduction(rawDamage, special.power);
          }

          // Blindagem das cartas do draft. Entra aqui, na única porta de dano
          // do jogador, para valer igualmente contra bala, mina, lava e chefe.
          if (isInfiniteMode(get())) {
            const run = useInfiniteRun.getState();
            if (run.modifiers.armor > 0) {
              const reduced = (playerTank.health - health) * (1 - run.modifiers.armor);
              health = playerTank.health - Math.max(1, Math.round(reduced));
            }
            // O contrato "sem levar dano" falha aqui — depois de todas as
            // reduções, para que um golpe totalmente absorvido não conte.
            if (playerTank.health - health > 0) {
              run.pushContractEvent({ type: "playerDamaged" });
            }
          }
        }

        // [BESTIÁRIO] Fênix: intercepta a morte uma vez por partida e renasce
        // no lugar com fração do HP + invulnerabilidade breve (regra idêntica
        // à do servidor Colyseus para o multiplayer).
        if (health <= 0 && !isMultiplayer) {
          const special = getPlayerTankSpecial();
          if (special?.type === "rebirth" && !get().playerRebirthUsed) {
            const now = Date.now();
            const rebornHealth = getRebirthHealth(playerTank.maxHealth, special.power);
            set({
              playerTank: { ...playerTank, health: rebornHealth, isAlive: true },
              playerRebirthUsed: true,
              playerRebirthInvulnUntil: now + REBIRTH_INVULN_MS,
              lastRebirthAt: now,
              lastDamageTime: now,
              lastDamageAmount: playerTank.health,
            });
            // Erupção de fogo do renascimento no lugar onde a Fênix caiu
            const { x, z } = playerTank.position;
            get().addExplosion({ x, y: 0.5, z }, "#fb923c", { source: "ability", ownerId: "player" });
            get().addExplosion({ x, y: 1.2, z }, "#fde047", { source: "ability", ownerId: "player" });
            return;
          }
        }

        const damageAmount = playerTank.health - health;
        const newTank = { ...playerTank, health, isAlive: health > 0 };

        // Registrar dano para feedback visual
        if (damageAmount > 0) {
          set({
            playerTank: newTank,
            lastDamageTime: Date.now(),
            lastDamageAmount: damageAmount
          });
        } else {
          set({ playerTank: newTank });
        }

        if (health <= 0) {
          // [FIX] Em multiplayer, NÃO chamar endGame(false).
          // O servidor Colyseus é autoritativo: a morte do jogador é tratada
          // via mensagem "playerDefeated" que ativa o modo espectador.
          // Chamar endGame(false) setava phase:"gameOver" com o jogo ainda ativo
          // no servidor, causando conflitos de estado e React error 185.
          if (isMultiplayer) {
            // Apenas atualizar o tank como morto (isAlive já é false no newTank).
            // O fluxo de espectador é controlado pelo evento playerDefeated do servidor.
            return;
          }

          if (gameMode === "battleRoyale") {
            const aliveCount = enemyTanks.filter(e => e.isAlive).length;
            const playerRank = aliveCount + 1;
            set({
              brPlayerRank: playerRank,
              brAliveCount: aliveCount,
            });
          } else if (gameMode === "genai") {
            // GenAI mode: partida única, sem ondas
            const { isAiVsAi, genAIPersonality, genAIPersonality2, score } = get();
            if (isAiVsAi) {
              // No modo AI vs AI, o outro AI venceu
              const winner = enemyTanks.find(e => e.isAlive);
              get().setGenAIWinner({
                name: winner?.name || "IA 2",
                isPlayer: false,
                personality: genAIPersonality2 || "strategic",
                provider: get().genAIProvider2 || undefined,
              });
            } else {
              // Jogador perdeu para IA - registrar resultado da partida
              const ai = enemyTanks[0];
              const playerKills = score; // score = kills do jogador
              const aiKills = 1; // IA matou o jogador
              get().submitGenAIMatchResult(true, aiKills, playerKills);

              get().setGenAIWinner({
                name: ai?.name || "IA",
                isPlayer: false,
                personality: genAIPersonality || "strategic",
                provider: get().genAIProvider1 || undefined,
              });
            }
            set({ phase: "genAIVictory" as GamePhase });
          } else {
            get().endGame(false);
          }
        }
      }
    },
    
    addBullet: (bullet) => {
      const state = get();
      // [PERF] Teto de balas simultâneas: com 50+ bots atirando não havia
      // limite — cada bala é um componente + entrada no array reconstruído.
      // Ao estourar o teto, descarta a bala mais antiga que NÃO é do jogador
      // (a do jogador nunca é sacrificada).
      if (state.bullets.length >= MAX_ACTIVE_BULLETS) {
        const oldestIdx = state.bullets.findIndex((b) => b.ownerId !== "player");
        if (oldestIdx >= 0) {
          state.bullets.splice(oldestIdx, 1);
        }
      }
      // Poder de linhagem viaja com a bala do jogador (modos locais).
      // Carimbar aqui centraliza a regra: todos os controladores
      // (touch/teclado/gamepad) passam por addBullet.
      const isLocalPlayerBullet = !state.isMultiplayer && bullet.ownerId === "player";
      if (isLocalPlayerBullet) {
        const special = getPlayerTankSpecial();
        if (
          special &&
          (special.type === "explosive" ||
            special.type === "piercing" ||
            special.type === "homing" ||
            special.type === "burn")
        ) {
          bullet.tankSpecial = special.type;
          bullet.tankSpecialPower = special.power;
          if (special.type === "piercing") {
            bullet.pierceLeft = getPierceCount(special.power);
            bullet.hitIds = [];
          }
        }
        // Atirar revela o tanque: quebra a furtividade e reinicia o timer.
        set({
          bullets: [...state.bullets, bullet],
          lastPlayerShotAt: Date.now(),
          ...(state.playerStealthed ? { playerStealthed: false } : {}),
        });
        return;
      }
      set({ bullets: [...state.bullets, bullet] });
    },
    
    removeBullet: (id) => {
      set((state) => ({
        bullets: state.bullets.filter((b) => b.id !== id),
      }));
    },
    
    damageEnemy: (id, damage, source = "player") => {
      const byPlayer = source === "player";
      // Defer cross-store update to avoid React Error #300
      if (byPlayer) {
        queueMicrotask(() => {
          useDailyMissions.getState().updateProgress('deal_damage', damage);
        });
      }
      // Quem morreu pelo chão, e onde — a explosão precisa do lugar, e o aviso
      // de eliminação precisa do nome.
      let terrainVictim: { name: string; x: number; z: number } | null = null;
      
      // Track what happened so we can handle side effects AFTER set()
      let phaseAtKill: string | null = null;
      let gameModeAtKill: string | null = null;
      let wasLastEnemy = false;
      let isDungeonStoryKill = false;
      let killedBoss = false;
      // Dados do abate para os contratos de onda: só são lidos depois do set(),
      // porque emitir eventos de dentro do callback dispararia um set aninhado.
      let killInfo: { kind: EnemyKind; distance: number; explodes: boolean; x: number; z: number } | null = null;
      let genAIStateAtKill: {
        isAiVsAi: boolean;
        playerTankName: string | undefined;
        genAIPersonality: string | null;
        genAIProvider1: string | null;
        killsThisWave: number;
      } | null = null;
      
      set((state) => {
        const enemies = state.enemyTanks.map((enemy) => {
          if (enemy.id === id && enemy.isAlive) {
            const newHealth = Math.max(0, enemy.health - damage);
            return { ...enemy, health: newHealth, isAlive: newHealth > 0 };
          }
          return enemy;
        });
        
        const killedEnemy = state.enemyTanks.find((e) => e.id === id && e.isAlive);
        const newEnemy = enemies.find((e) => e.id === id);
        
        let newScore = state.score;
        let newKills = state.killsThisWave;
        let updates: Partial<typeof state> = {};
        
        if (killedEnemy && newEnemy && !newEnemy.isAlive) {
          if (byPlayer) {
            const basePoints = 100;
            const typeBonus = killedEnemy.enemyType === "boss" ? 500 :
                             killedEnemy.enemyType === "heavy" ? 200 :
                             killedEnemy.enemyType === "sniper" ? 150 :
                             killedEnemy.enemyType === "fast" ? 120 : 100;
            newScore += basePoints + typeBonus;
            newKills += 1;

            if (killedEnemy.enemyType === "boss") {
              killedBoss = true;
            }
          } else {
            terrainVictim = {
              name: killedEnemy.name,
              x: killedEnemy.position.x,
              z: killedEnemy.position.z,
            };
          }

          if (byPlayer && isInfiniteMode(state) && state.playerTank) {
            killInfo = {
              kind: (killedEnemy.enemyType ?? "normal") as EnemyKind,
              distance: Math.hypot(
                killedEnemy.position.x - state.playerTank.position.x,
                killedEnemy.position.z - state.playerTank.position.z,
              ),
              // Modificador de bloco "voláteis". Sem isto o modificador era
              // anunciado na abertura da onda e não fazia absolutamente nada.
              explodes: killedEnemy.explodesOnDeath === true,
              x: killedEnemy.position.x,
              z: killedEnemy.position.z,
            };
          }

          if (state.gameMode === "battleRoyale") {
            const aliveCount = enemies.filter(e => e.isAlive).length + (state.playerTank?.isAlive ? 1 : 0);
            updates.brAliveCount = aliveCount;
            
            // Se o alvo do espectador morreu, focar em quem está ganhando (mais HP)
            if (state.brIsSpectating && state.brSpectatingTargetId === id) {
              const aliveBots = enemies.filter(e => e.isAlive);
              if (aliveBots.length > 0) {
                const sortedByHealth = [...aliveBots].sort((a, b) => b.health - a.health);
                updates.brSpectatingTargetId = sortedByHealth[0].id;
              } else {
                updates.brSpectatingTargetId = null;
                updates.brIsSpectating = false;
              }
            }
          }
        }
        
        const aliveEnemies = enemies.filter((e) => e.isAlive);
        if (aliveEnemies.length === 0) {
          // DON'T call setTimeout/set here — just record what happened
          phaseAtKill = state.phase;
          gameModeAtKill = state.gameMode;
          wasLastEnemy = true;
          isDungeonStoryKill =
            state.phase === "storyPlaying" &&
            (state.currentDungeonRoomId !== null ||
              (state.storyChapterId !== null && chapterHasDungeon(state.storyChapterId)));
          
          // Capture genAI state if needed
          if (state.phase === "playing" && state.gameMode === "genai") {
            genAIStateAtKill = {
              isAiVsAi: state.isAiVsAi,
              playerTankName: state.playerTank?.name,
              genAIPersonality: state.genAIPersonality,
              genAIProvider1: state.genAIProvider1,
              killsThisWave: newKills, // Use the updated kills count
            };
          }
        }
        
        return { enemyTanks: enemies, score: newScore, killsThisWave: newKills, ...updates };
      });
      
      // Quem queimou até o fim não deixa estado no registro: o id de inimigo se
      // repete entre ondas, e o tanque seguinte nasceria em chamas.
      const burned = terrainVictim as { name: string; x: number; z: number } | null;
      if (burned) {
        releaseTankSurface(id);
        // A explosão de morte sai da bala que acertou — então um tanque morto
        // pelo chão sumiria em silêncio, de um quadro para o outro, e a única
        // leitura possível seria "bugou". O terreno mata com o mesmo estouro,
        // em tom de brasa.
        get().addExplosion({ x: burned.x, y: 0.6, z: burned.z }, "#ff7a1a", {
          source: "environment",
        });
        useAudio.getState().playTerrainSfx("terrain_ignite", 0.7);
        if (get().gameMode === "battleRoyale") {
          get().addBREliminationFeed(burned.name, TERRAIN_KILLER_NAME);
        }
      }

      // Defer boss kill tracking to avoid React Error #300
      if (killedBoss) {
        queueMicrotask(() => {
          useDailyMissions.getState().updateProgress("defeat_bosses", 1);
        });
      }
      
      // Handle phase transitions OUTSIDE set() to avoid React Error #300
      // Re-alias genAIStateAtKill: TS can't track mutations inside set() callback,
      // so it erroneously narrows the variable to `never` after the closure.
      const capturedGenAI = genAIStateAtKill as {
        isAiVsAi: boolean;
        playerTankName: string | undefined;
        genAIPersonality: string | null;
        genAIProvider1: string | null;
        killsThisWave: number;
      } | null;

      // ---- Batalha Infinita: contrato e roubo de vida ----
      const capturedKill = killInfo as
        | { kind: EnemyKind; distance: number; explodes: boolean; x: number; z: number }
        | null;
      if (capturedKill) {
        const run = useInfiniteRun.getState();
        run.pushContractEvent({
          type: "enemyKilled",
          kind: capturedKill.kind,
          distance: capturedKill.distance,
          at: Date.now(),
          burning: Date.now() < getTankSurfaceStatus(PLAYER_SURFACE_ID).burningUntil,
        });

        // Inimigo volátil: estoura ao morrer. O dano cai com a distância, então
        // abater de longe é a resposta — o modificador muda COMO se joga o
        // bloco, que é exatamente o ponto de escalar por comportamento.
        if (capturedKill.explodes) {
          const player = get().playerTank;
          get().addExplosion(
            new THREE.Vector3(capturedKill.x, 0.6, capturedKill.z),
            "#ff7a1a",
            { source: "environment" },
          );
          if (player?.isAlive && capturedKill.distance < VOLATILE_BLAST_RADIUS) {
            const falloff = 1 - capturedKill.distance / VOLATILE_BLAST_RADIUS;
            const damage = Math.max(1, Math.round(VOLATILE_BLAST_DAMAGE * falloff));
            get().updatePlayerHealth(player.health - damage);
          }
        }

        const lifesteal = run.modifiers.lifestealPerKill;
        if (lifesteal > 0) {
          set((state) =>
            state.playerTank && state.playerTank.isAlive
              ? {
                  playerTank: {
                    ...state.playerTank,
                    health: Math.min(
                      state.playerTank.maxHealth,
                      state.playerTank.health + lifesteal,
                    ),
                  },
                }
              : {},
          );
        }
      }

      if (wasLastEnemy) {
        if (phaseAtKill === "storyPlaying") {
          // Em capítulos de dungeon, concluir sala não deve encerrar capítulo.
          // A vitória é controlada por useDungeonNavigation quando a última sala termina.
          if (!isDungeonStoryKill) {
            setTimeout(() => set({ phase: "storyVictory" }), 500);
          }
        } else if (phaseAtKill === "playing" && gameModeAtKill === "genai" && capturedGenAI) {
          // GenAI mode: partida única termina quando inimigo morre
          if (capturedGenAI.isAiVsAi) {
            // IA 1 venceu
            get().setGenAIWinner({
              name: capturedGenAI.playerTankName || "IA 1",
              isPlayer: false,
              personality: capturedGenAI.genAIPersonality || "strategic",
              provider: capturedGenAI.genAIProvider1 || undefined,
            });
          } else {
            // Jogador venceu - registrar resultado da partida
            const playerKills = capturedGenAI.killsThisWave; // Already includes the last kill
            const aiKills = 0; // Jogador ainda está vivo
            get().submitGenAIMatchResult(false, aiKills, playerKills);
            
            get().setGenAIWinner({
              name: "Você",
              isPlayer: true,
              personality: "player",
            });
          }
          setTimeout(() => set({ phase: "genAIVictory" as GamePhase }), 500);
        } else if (phaseAtKill === "playing" && gameModeAtKill !== "battleRoyale") {
          setTimeout(() => get().endGame(true), 500);
        }
      }
    },
    
    // [BR][PERF] Versão em lote do dano da parede: antes o controlador chamava
    // damageEnemy() por vítima — até 50 set()/reconstruções do array de
    // inimigos a cada tick de 200ms no fim da partida. Um único set() aplica
    // o dano a todos fora da zona, preservando o comportamento original
    // (pontos por eliminação, contagem de vivos, retarget do espectador,
    // feed de eliminações e progresso de missão).
    applyBRZoneDamage: (halfSize, damage) => {
      const eliminatedNames: string[] = [];
      let totalDamage = 0;

      set((state) => {
        let changed = false;
        let newScore = state.score;
        let newKills = state.killsThisWave;

        const enemies = state.enemyTanks.map((enemy) => {
          if (!enemy.isAlive) return enemy;
          if (Math.abs(enemy.position.x) <= halfSize && Math.abs(enemy.position.z) <= halfSize) {
            return enemy;
          }
          changed = true;
          totalDamage += damage;
          const newHealth = Math.max(0, enemy.health - damage);
          if (newHealth <= 0) {
            eliminatedNames.push(enemy.name);
            const typeBonus = enemy.enemyType === "boss" ? 500 :
                              enemy.enemyType === "heavy" ? 200 :
                              enemy.enemyType === "sniper" ? 150 :
                              enemy.enemyType === "fast" ? 120 : 100;
            newScore += 100 + typeBonus;
            newKills += 1;
          }
          return { ...enemy, health: newHealth, isAlive: newHealth > 0 };
        });

        if (!changed) return {};

        const updates: Partial<typeof state> = {
          enemyTanks: enemies,
          score: newScore,
          killsThisWave: newKills,
        };

        if (eliminatedNames.length > 0 && state.gameMode === "battleRoyale") {
          updates.brAliveCount =
            enemies.filter((e) => e.isAlive).length + (state.playerTank?.isAlive ? 1 : 0);

          if (state.brIsSpectating && state.brSpectatingTargetId) {
            const target = enemies.find((e) => e.id === state.brSpectatingTargetId);
            if (target && !target.isAlive) {
              const aliveBots = enemies.filter((e) => e.isAlive).sort((a, b) => b.health - a.health);
              updates.brSpectatingTargetId = aliveBots[0]?.id ?? null;
              if (aliveBots.length === 0) updates.brIsSpectating = false;
            }
          }
        }

        return updates;
      });

      if (totalDamage > 0) {
        queueMicrotask(() => {
          useDailyMissions.getState().updateProgress("deal_damage", totalDamage);
        });
      }
      eliminatedNames.forEach((name) => get().addBREliminationFeed(name, "Parede Elétrica"));
    },

    /**
     * Dano de terreno num inimigo — lava sob as esteiras, chamas grudadas.
     *
     * Delega ao caminho único de dano com a origem trocada. Ter tido, por um
     * momento, uma implementação paralela custou caro: o fim de onda mora
     * dentro de `damageEnemy` ("morreu o último, a onda acabou"), então o
     * último inimigo da onda morrendo afogado na lava deixava o jogador preso
     * numa arena vazia, esperando um inimigo que não existia mais.
     */
    damageEnemyByTerrain: (id, damage) => {
      if (damage <= 0) return;
      get().damageEnemy(id, damage, "terrain");
    },

    removeEnemy: (id) => {
      set((state) => ({
        enemyTanks: state.enemyTanks.filter((e) => e.id !== id),
      }));
    },
    
    updateEnemyPosition: (id, pos) => {
      // Hot path (cada inimigo, cada frame): antes reconstruía o array inteiro
      // com objetos novos — O(N²) de alocações por frame e re-render global.
      // Posição é lida via getState() nos loops de frame, então mutar o
      // Vector3 existente é suficiente e não quebra reatividade de vida/morte.
      const { enemyTanks } = get();
      for (let i = 0; i < enemyTanks.length; i++) {
        if (enemyTanks[i].id === id) {
          enemyTanks[i].position.copy(pos);
          return;
        }
      }
    },
    
    damageObstacle: (id, damage) => {
      let destroyed = false;
      set((state) => ({
        obstacles: state.obstacles.map((obs) => {
          if (obs.id === id && obs.type === "destructible") {
            const newHealth = Math.max(0, obs.health - damage);
            // A transição para zero acontece uma vez só: contar aqui impede que
            // tiros posteriores no mesmo escombro inflem o contrato.
            if (newHealth <= 0 && obs.health > 0) destroyed = true;
            return { ...obs, health: newHealth };
          }
          return obs;
        }),
      }));

      if (destroyed && isInfiniteMode(get())) {
        useInfiniteRun.getState().pushContractEvent({ type: "obstacleDestroyed" });
      }
    },
    
    removeObstacle: (id) => {
      set((state) => ({
        obstacles: state.obstacles.filter((o) => o.id !== id),
      }));
    },
    
    addScore: (points) => {
      set((state) => ({ score: state.score + points }));
    },
    
    addExplosion: (position, color, options) => {
      set((state) => ({
        explosions: [
          ...state.explosions,
          {
            position,
            color,
            createdAt: Date.now(),
            cosmeticEffectId: options?.cosmeticEffectId ?? null,
            cosmeticEffectType: options?.cosmeticEffectType ?? null,
            source: options?.source,
            ownerId: options?.ownerId ?? null,
          },
        ]
      }));
    },
    
    clearExplosions: () => {
      set({ explosions: [] });
    },
    
    addItem: (item) => {
      set((state) => ({ items: [...state.items, item] }));
    },
    
    removeItem: (id) => {
      set((state) => ({ items: state.items.filter((i) => i.id !== id) }));
    },
    
    collectItem: (id) => {
      const { items, heldItem, storyNoHealing, isMultiplayer } = get();
      if (isMultiplayer) return;
      const item = items.find((i) => i.id === id);
      if (!item) return;

      queueMicrotask(() => {
        useDailyMissions.getState().updateProgress("collect_powerups", 1);
      });

      // Flash curto na cor do item ao coletar — antes o item simplesmente
      // desaparecia sem nenhum feedback visual.
      const willConsume = item.type === "heal" || !heldItem;
      if (willConsume) {
        get().addExplosion(
          { x: item.position.x, y: 0.9, z: item.position.z },
          ITEM_CONFIG[item.type].color,
          { source: "ability" },
        );
      }

      if (item.type === "heal") {
        // Block healing in Chapter 4 (storyNoHealing mode)
        if (storyNoHealing) {
          // Remove the item but don't heal
          set((state) => ({
            items: state.items.filter((i) => i.id !== id),
          }));
          return;
        }
        const { playerTank } = get();
        if (playerTank) {
          const healAmount = 30;
          const newHealth = Math.min(playerTank.maxHealth, playerTank.health + healAmount);
          set((state) => ({
            items: state.items.filter((i) => i.id !== id),
            playerTank: state.playerTank ? { ...state.playerTank, health: newHealth } : null,
          }));
        }
      } else if (!heldItem) {
        set((state) => ({
          items: state.items.filter((i) => i.id !== id),
          heldItem: item.type,
        }));
      }
    },
    
    useHeldItem: () => {
      const { heldItem, playerTank, enemyTanks, arenaSize, storyNoHealing, isMultiplayer, gameMode, phase } = get();
      if (!heldItem || !playerTank) return;
      if (phase === "storyPlaying") return;

      if (isMultiplayer) {
        useColyseus.getState().sendUseItem();
        return;
      }

      queueMicrotask(() => {
        useDailyMissions.getState().updateProgress("use_items", 1);
      });

      if (isInfiniteMode(get())) {
        useInfiniteRun.getState().pushContractEvent({ type: "itemUsed" });
      }

      const config = ITEM_CONFIG[heldItem];
      const now = Date.now();
      const balance = getMagicItemBalance(gameMode, isMultiplayer);
      
      if (heldItem === "shield") {
        set({
          heldItem: null,
          activeEffect: { type: "shield", expiresAt: now + config.duration, shieldHits: 4 },
        });
      } else if (heldItem === "heal") {
        // Block healing in Chapter 4 (storyNoHealing mode)
        if (storyNoHealing) {
          // Consume item but don't heal
          set({ heldItem: null });
          return;
        }
        // Cura instantânea - não precisa de efeito ativo
        const newHealth = Math.min(playerTank.health + 30, playerTank.maxHealth);
        set({
          heldItem: null,
          playerTank: { ...playerTank, health: newHealth },
        });
      } else if (heldItem === "emp") {
        // EMP - desabilita inimigos próximos por 3 segundos
        const empRadius = 15;
        const affectedEnemies = enemyTanks.filter(enemy => {
          const dx = enemy.position.x - playerTank.position.x;
          const dz = enemy.position.z - playerTank.position.z;
          return Math.sqrt(dx * dx + dz * dz) < empRadius && enemy.isAlive;
        });
        
        // Congela todos os inimigos afetados
        const newFrozen = new Map(get().frozenEnemies);
        affectedEnemies.forEach(enemy => {
          newFrozen.set(enemy.id, now + 3000);
        });

        set({
          heldItem: null,
          frozenEnemies: newFrozen,
        });

        // Feedback visual do pulso: onda azul no jogador + faísca em cada
        // inimigo atingido (antes o EMP acontecia sem nenhum efeito na tela).
        get().addExplosion(
          { x: playerTank.position.x, y: 0.6, z: playerTank.position.z },
          "#3b82f6",
          { source: "kill" },
        );
        affectedEnemies.forEach((enemy) => {
          get().addExplosion(
            { x: enemy.position.x, y: 0.6, z: enemy.position.z },
            "#60a5fa",
            { source: "ability" },
          );
        });
        useAudio.getState().playExplosion();
      } else if (heldItem === "airstrike") {
        // Ataque aéreo - dano em área aleatória com inimigos
        const boundary = arenaSize / 2 - 10;
        
        // Encontra área com mais inimigos
        let bestX = 0, bestZ = 0, bestCount = 0;
        for (let i = 0; i < 10; i++) {
          const testX = (Math.random() - 0.5) * boundary * 2;
          const testZ = (Math.random() - 0.5) * boundary * 2;
          const count = enemyTanks.filter(e => {
            const dx = e.position.x - testX;
            const dz = e.position.z - testZ;
            return Math.sqrt(dx * dx + dz * dz) < 12 && e.isAlive;
          }).length;
          if (count > bestCount) {
            bestCount = count;
            bestX = testX;
            bestZ = testZ;
          }
        }
        
        // Causa dano a todos na área
        const airstrikeRadius = 12;
        const airstrikeDamage = 50;
        const struckEnemies = enemyTanks.filter((enemy) => {
          const dx = enemy.position.x - bestX;
          const dz = enemy.position.z - bestZ;
          return enemy.isAlive && Math.sqrt(dx * dx + dz * dz) < airstrikeRadius;
        });

        set({ heldItem: null });

        // damageEnemy (em vez de mutar enemyTanks direto) garante que kills do
        // ataque aéreo contem pontuação, kills da wave e contagem de vivos no BR.
        struckEnemies.forEach((enemy) => {
          get().addExplosion(
            { x: enemy.position.x, y: 0.6, z: enemy.position.z },
            "#f97316",
            { source: "kill" },
          );
          get().damageEnemy(enemy.id, airstrikeDamage);
        });

        // Marca a zona do bombardeio mesmo sem inimigos atingidos.
        get().addExplosion({ x: bestX, y: 0.6, z: bestZ }, "#f97316", { source: "kill" });
        get().addExplosion(
          { x: bestX + 4, y: 0.6, z: bestZ - 3 },
          "#fbbf24",
          { source: "environment" },
        );
        get().addExplosion(
          { x: bestX - 3.5, y: 0.6, z: bestZ + 3.5 },
          "#fbbf24",
          { source: "environment" },
        );
        useAudio.getState().playExplosion();
      } else if (heldItem === "gravityWell") {
        set({
          heldItem: null,
          activeEffect: {
            type: "gravityWell",
            expiresAt: now + balance.gravityDuration,
            origin: { x: playerTank.position.x, z: playerTank.position.z },
          },
        });
      } else if (heldItem === "timeDilation") {
        set({
          heldItem: null,
          activeEffect: {
            type: "timeDilation",
            expiresAt: now + balance.timeDuration,
            origin: { x: playerTank.position.x, z: playerTank.position.z },
          },
        });
      } else if (heldItem === "echoShield") {
        set({
          heldItem: null,
          activeEffect: {
            type: "echoShield",
            expiresAt: now + balance.echoDuration,
            shieldHits: 1,
          },
        });
      } else if (heldItem === "decoyHologram") {
        const aim = get().aimDirection;
        const hasAim = aim.lengthSq() > 0.01;
        const angle = hasAim
          ? Math.atan2(aim.x, aim.y)
          : playerTank.rotation + (Math.random() - 0.5) * 0.8;
        const arenaBoundary = arenaSize / 2 - 2;
        const decoyX = Math.max(-arenaBoundary, Math.min(arenaBoundary, playerTank.position.x + Math.sin(angle) * balance.decoyOffset));
        const decoyZ = Math.max(-arenaBoundary, Math.min(arenaBoundary, playerTank.position.z + Math.cos(angle) * balance.decoyOffset));

        set({
          heldItem: null,
          activeEffect: {
            type: "decoyHologram",
            expiresAt: now + balance.decoyDuration,
            decoyPosition: { x: decoyX, z: decoyZ },
          },
        });
      } else if (heldItem === "shockwave") {
        const centerX = playerTank.position.x;
        const centerZ = playerTank.position.z;
        const arenaBoundary = arenaSize / 2 - 2;

        const newFrozen = new Map(get().frozenEnemies);
        const pushedEnemies = enemyTanks.map((enemy) => {
          if (!enemy.isAlive) return enemy;
          const dx = enemy.position.x - centerX;
          const dz = enemy.position.z - centerZ;
          const dist = Math.sqrt(dx * dx + dz * dz);
          if (dist <= 0.001 || dist > balance.shockwaveRadius) return enemy;

          const influence = 1 - dist / balance.shockwaveRadius;
          const push = balance.shockwaveForce * influence;
          const nx = dx / dist;
          const nz = dz / dist;
          const pushedX = Math.max(-arenaBoundary, Math.min(arenaBoundary, enemy.position.x + nx * push));
          const pushedZ = Math.max(-arenaBoundary, Math.min(arenaBoundary, enemy.position.z + nz * push));

          newFrozen.set(enemy.id, now + balance.shockwaveFreezeMs);
          return {
            ...enemy,
            position: new THREE.Vector3(pushedX, enemy.position.y, pushedZ),
          };
        });

        set((state) => ({
          heldItem: null,
          enemyTanks: pushedEnemies,
          frozenEnemies: newFrozen,
          bullets: state.bullets.map((bullet) => {
            if (bullet.ownerId === "player") return bullet;
            const dx = bullet.position.x - centerX;
            const dz = bullet.position.z - centerZ;
            const dist = Math.sqrt(dx * dx + dz * dz);
            if (dist <= 0.001 || dist > balance.shockwaveRadius) return bullet;

            const influence = 1 - dist / balance.shockwaveRadius;
            const outward = new THREE.Vector3(dx / dist, 0, dz / dist).normalize();
            const redirected = bullet.direction.clone().lerp(outward, 0.72).normalize();
            return {
              ...bullet,
              direction: redirected,
              speed: bullet.speed * (1 + influence * 0.35),
            };
          }),
        }));

        get().addExplosion({ x: centerX, y: 0.6, z: centerZ }, "#f43f5e");
      } else {
        // Itens com duração (intense, freeze, turbo, invisibility, vampire, minigun, magnet)
        set({
          heldItem: null,
          activeEffect: { type: heldItem, expiresAt: now + config.duration },
        });
      }
    },
    
    setActiveEffect: (effect) => {
      set({ activeEffect: effect });
    },

    setHeldItem: (item) => {
      set({ heldItem: item });
    },

    syncMultiplayerItems: (items) => {
      set({ items });
    },

    syncMultiplayerPowerupState: ({ heldItem, activeEffect }) => {
      set({ heldItem, activeEffect });
    },
    
    freezeEnemy: (enemyId, duration) => {
      const now = Date.now();
      set((state) => {
        const newFrozen = new Map(state.frozenEnemies);
        newFrozen.set(enemyId, now + duration);
        return { frozenEnemies: newFrozen };
      });
    },
    
    isEnemyFrozen: (enemyId) => {
      const { frozenEnemies } = get();
      const expiresAt = frozenEnemies.get(enemyId);
      if (!expiresAt) return false;
      return Date.now() < expiresAt;
    },
    
    blockBulletWithShield: () => {
      const { activeEffect } = get();
      if (!activeEffect || (activeEffect.type !== "shield" && activeEffect.type !== "echoShield")) return false;
      
      const baseHits = activeEffect.type === "echoShield" ? 1 : (activeEffect.shieldHits || 0);
      const hitsLeft = baseHits - 1;
      if (hitsLeft <= 0) {
        set({ activeEffect: null });
      } else {
        set({ activeEffect: { ...activeEffect, shieldHits: hitsLeft } });
      }
      return true;
    },
    
    spawnRandomItem: () => {
      const { arenaSize, obstacles, items, playerTank, enemyTanks, gameMode } = get();
      if (items.length >= 8) return;
      
      // Itens base para todos os modos (exceto story)
      const baseItems: ItemType[] = [
        "intense",
        "freeze",
        "shield",
        "heal",
        "turbo",
        "gravityWell",
        "timeDilation",
        "echoShield",
        "decoyHologram",
        "shockwave",
      ];
      // Itens originalmente do BR que agora também podem aparecer fora do story
      const specialItems: ItemType[] = [
        "invisibility",
        "emp",
        "vampire",
        "airstrike",
        "minigun",
        "magnet",
      ];
      
      // BR recebe levemente mais itens especiais para manter o caos do modo
      const availableItems = gameMode === "battleRoyale"
        ? [...baseItems, ...specialItems, ...specialItems]
        : [...baseItems, ...specialItems];
      
      const randomType = availableItems[Math.floor(Math.random() * availableItems.length)];
      
      const boundary = arenaSize / 2 - 3;
      let position: THREE.Vector3 | null = null;
      
      for (let attempt = 0; attempt < 30; attempt++) {
        const x = (Math.random() - 0.5) * boundary * 2;
        const z = (Math.random() - 0.5) * boundary * 2;
        const testPos = new THREE.Vector3(x, 0, z);
        
        let valid = true;

        // Item no fundo de uma poça de lava é uma isca, não uma recompensa: o
        // jogador paga vida pelo que devia ajudá-lo. Nas arenas sem terreno a
        // lista está vazia e o teste custa nada.
        if (arenaSurface.active && isInsideHazard(testPos.x, testPos.z, arenaSurface.zones, 1)) {
          valid = false;
        }

        if (valid) {
          for (const obs of obstacles) {
            const dx = Math.abs(testPos.x - obs.position.x);
            const dz = Math.abs(testPos.z - obs.position.z);
            if (dx < obs.size.x / 2 + 1.5 && dz < obs.size.z / 2 + 1.5) {
              valid = false;
              break;
            }
          }
        }
        
        if (valid && playerTank) {
          const dx = testPos.x - playerTank.position.x;
          const dz = testPos.z - playerTank.position.z;
          if (Math.sqrt(dx * dx + dz * dz) < 5) valid = false;
        }
        
        if (valid) {
          for (const enemy of enemyTanks) {
            const dx = testPos.x - enemy.position.x;
            const dz = testPos.z - enemy.position.z;
            if (Math.sqrt(dx * dx + dz * dz) < 3) {
              valid = false;
              break;
            }
          }
        }
        
        if (valid) {
          for (const existingItem of items) {
            const dx = testPos.x - existingItem.position.x;
            const dz = testPos.z - existingItem.position.z;
            if (Math.sqrt(dx * dx + dz * dz) < 4) {
              valid = false;
              break;
            }
          }
        }
        
        if (valid) {
          position = testPos;
          break;
        }
      }
      
      if (position) {
        const newItem: ItemData = {
          id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: randomType,
          position,
          createdAt: Date.now(),
        };
        set((state) => ({ items: [...state.items, newItem] }));
      }
    },
    
    setSelectedTerrain: (terrain: TerrainConfig | null) => {
      set({ selectedTerrain: terrain });
    },
    
    initializeGame: (terrainOverride?: TerrainConfig) => {
      const terrain = terrainOverride || get().selectedTerrain;
      get().initializeWave(1, terrain || undefined);
    },
    
    initializeWave: (waveNumber: number, terrainOverride?: TerrainConfig) => {
      const infinite = isInfiniteMode(get());
      const run = useInfiniteRun.getState();

      // ---- Forma da onda ----
      // Fora do modo infinito nada disto se aplica e a onda sai idêntica à de
      // antes: `shaping` fica undefined e a semente volta a ser a legada.
      let shaping: WaveShaping | undefined;
      let waveSeed: number | undefined;

      if (infinite) {
        const previous = get().playerTank;
        const performance = previous
          ? adaptiveDifficultyMultiplier(
              previous.health,
              previous.maxHealth,
              get().killsThisWave,
              get().waveConfig?.enemyCount ?? 0,
              run.waveStartedAt > 0 ? (Date.now() - run.waveStartedAt) / 1000 : 45,
            )
          : 1;

        // Peso da build: cada carta escolhida deixa o tanque mais forte, e sem
        // contrapartida a onda 25 seria mais fácil que a 10. O acréscimo é
        // deliberadamente pequeno (teto de +18% com 12 cartas) — a graça do
        // draft é sentir-se poderoso, e anular isso ponto a ponto seria punir o
        // jogador por jogar bem.
        const buildPower = 1 + Math.min(0.18, run.chosenCardIds.length * 0.015);
        const adaptive = Math.min(1.35, performance * buildPower);

        shaping = {
          kind: getWaveKind(waveNumber),
          worldModifier: run.worldModifierFor(waveNumber),
          adaptive,
        };
        // Semente da corrida, não da onda: duas partidas nunca repetem a mesma
        // sequência de arenas.
        waveSeed = infiniteSeed("wave", run.runSeed, waveNumber);
      }

      const waveConfig = generateWaveConfig(waveNumber, terrainOverride, waveSeed, shaping);
      const random = seededRandom(
        // No modo infinito o layout acompanha a semente da corrida; nos demais
        // modos segue o comportamento antigo (parcialmente aleatório por relógio).
        infinite ? infiniteSeed("layout", run.runSeed, waveNumber) : waveNumber * 7919 + Date.now() % 1000,
      );

      const rawObstacles = generateArenaObstacles(waveConfig, random);

      // ---- Terreno: água, lava, gelo, lama ----
      // O plano é o mesmo em qualquer modo; o que muda é o perfil que
      // `planArenaTerrain` aplica. Aqui a Batalha Infinita entra com a onda (é
      // ela que libera os perigos aos poucos) e com a semente da corrida, para
      // que duas partidas nunca repitam a mesma sequência de arenas.
      const terrainPlan = planArenaTerrain({
        mode: "infinite",
        // Esta entrada é a arena da Batalha Infinita. Quando a onda vem de
        // outro caminho (partida em grupo por ondas, reinício legado) não há
        // corrida para semear e o terreno fica desligado, como sempre esteve.
        disabled: !infinite,
        seed: infiniteSeed("terrain", run.runSeed, waveNumber),
        arenaSize: waveConfig.arenaSize,
        wave: waveNumber,
        recentArchetypes: infinite ? run.archetypeHistory : undefined,
        calmArena: getWaveKind(waveNumber) === "boss",
      });
      const archetype = terrainPlan.archetype;
      const hazardZones = terrainPlan.zones;

      // Blocos dentro de um lago ou de uma poça de lava pareceriam bugs de
      // geração; o perigo tem de ser o obstáculo ali.
      const obstacles = hazardZones.length > 0
        ? rawObstacles.filter((obs) => !isInsideHazard(obs.position.x, obs.position.z, hazardZones, 0.8))
        : rawObstacles;

      const progressState = useProgress.getState();
      const selectedTankConfig = progressState.getSelectedTank();
      const selectedSkin = progressState.getSelectedSkin();
      const selectedEffect = progressState.getSelectedEffect();
      const maxTankScale = progressState.tanks.reduce((max, tank) => Math.max(max, tank.scale ?? 1), 1);
      const playerScale = selectedTankConfig.scale ?? 1;
      const playerRadius = 1.3 * playerScale;
      // Usa o maior tanque disponível como envelope de segurança para evitar spawn em bloco.
      const maxPlayerRadius = 1.3 * maxTankScale;
      const spawnClearance = maxPlayerRadius + 1.15;
      
      const isInArena = (pos: THREE.Vector3): boolean => {
        const { arenaShape, arenaSize } = waveConfig;
        const limit = arenaSize / 2 - 2;
        if (arenaShape === "circle") {
          return Math.sqrt(pos.x * pos.x + pos.z * pos.z) < limit;
        }
        if (arenaShape === "cross") {
          const armWidth = arenaSize / 4;
          return Math.abs(pos.x) < armWidth || Math.abs(pos.z) < armWidth;
        }
        if (arenaShape === "corridor") {
          return Math.abs(pos.x) < arenaSize / 6 || Math.abs(pos.z) < limit;
        }
        return Math.abs(pos.x) < limit && Math.abs(pos.z) < limit;
      };
      
      const isBlocked = (pos: THREE.Vector3): boolean => {
        if (!isInArena(pos)) return true;
        // Nascer dentro de lava ou de um lago seria perder vida (ou tempo) por
        // um sorteio, antes mesmo de o jogador tocar no controle.
        if (isInsideHazard(pos.x, pos.z, hazardZones, 1.5)) return true;
        for (const obs of obstacles) {
          const dx = Math.abs(pos.x - obs.position.x);
          const dz = Math.abs(pos.z - obs.position.z);
          const safeDistance = Math.max(obs.size.x, obs.size.z) / 2 + spawnClearance;
          if (dx < safeDistance && dz < safeDistance) {
            return true;
          }
        }
        return false;
      };
      
      const findSafePlayerSpawn = (): THREE.Vector3 => {
        const spawnAttempts = [
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(3, 0, 3),
          new THREE.Vector3(-3, 0, 3),
          new THREE.Vector3(3, 0, -3),
          new THREE.Vector3(-3, 0, -3),
          new THREE.Vector3(0, 0, 4),
          new THREE.Vector3(0, 0, -4),
          new THREE.Vector3(4, 0, 0),
          new THREE.Vector3(-4, 0, 0),
          new THREE.Vector3(5, 0, 5),
          new THREE.Vector3(-5, 0, 5),
          new THREE.Vector3(5, 0, -5),
          new THREE.Vector3(-5, 0, -5),
        ];
        
        for (const pos of spawnAttempts) {
          if (!isBlocked(pos)) return pos;
        }
        
        for (let attempt = 0; attempt < 24; attempt++) {
          const angle = (attempt / 24) * Math.PI * 2;
          const radius = 2 + (attempt % 4) * 2;
          const pos = new THREE.Vector3(
            Math.cos(angle) * radius,
            0,
            Math.sin(angle) * radius
          );
          if (!isBlocked(pos)) return pos;
        }
        
        return new THREE.Vector3(0, 0, 0);
      };
      
      const playerSpawn = findSafePlayerSpawn();
      const obstaclesCleared = obstacles.filter((obs) => {
        const dx = Math.abs(playerSpawn.x - obs.position.x);
        const dz = Math.abs(playerSpawn.z - obs.position.z);
        const clearDistance = Math.max(obs.size.x, obs.size.z) / 2 + maxPlayerRadius + 1.2;
        return !(dx < clearDistance && dz < clearDistance);
      });
      
      const enemies = generateEnemyTanks(waveConfig, obstaclesCleared, random);

      // Inimigo que nasce dentro da lava morre sozinho antes de o jogador
      // encostar nele — o abate não teria sido do jogador, e a onda pareceria
      // quebrada. Empurra para fora radialmente, mantendo-o na arena.
      if (hazardZones.length > 0) {
        for (const enemy of enemies) {
          const safe = pushOutOfHazards(
            enemy.position.x,
            enemy.position.z,
            hazardZones,
            waveConfig.arenaSize,
          );
          if (safe) enemy.position.set(safe.x, enemy.position.y, safe.z);
        }
      }

      // ---- Terreno vive fora do React ----
      // Tanque, IA e projétil consultam a superfície em useFrame; escrevê-la no
      // store re-renderizaria a cena inteira sem nenhum ganho. Onda nova zera o
      // registro de estado: ninguém entra na arena seguinte ainda em chamas.
      resetTankSurfaces();
      setArenaSurface(hazardZones, waveConfig.arenaSize);

      const currentHealth = get().playerTank?.health;
      // Cartas de casco somam vida máxima; sem cartas o bônus é zero e o
      // tanque sai exatamente com a vida do catálogo.
      const healthBonus = infinite ? run.modifiers.maxHealthBonus : 0;
      const maxHealth = Math.max(20, selectedTankConfig.health + healthBonus);
      const startHealth = currentHealth !== undefined && currentHealth <= maxHealth ? currentHealth : maxHealth;

      const playerTank: TankData = {
        id: "player",
        position: playerSpawn,
        rotation: 0,
        health: startHealth,
        maxHealth: maxHealth,
        color: selectedTankConfig.color,
        isPlayer: true,
        isAlive: true,
        name: selectedTankConfig.name,
        lastFireTime: 0,
        speed: selectedTankConfig.speed,
        damage: selectedTankConfig.damage,
        shotCount: selectedTankConfig.bulletCount ?? 1,
        size: selectedTankConfig.scale ?? 1,
        fireRate: selectedTankConfig.fireRate ?? 1,
        skinId: selectedSkin?.id || null,
        effectId: selectedEffect?.id || null,
      };
      
      set({
        waveConfig,
        arenaSize: waveConfig.arenaSize,
        arenaShape: waveConfig.arenaShape,
        terrain: waveConfig.terrain,
        playerTank,
        enemyTanks: enemies,
        bullets: [],
        obstacles: obstaclesCleared,
        explosions: [],
        moveDirection: new THREE.Vector2(0, 0),
        isFiring: false,
      });

      // ---- Contrato e abertura de onda ----
      if (infinite) {
        const kinds = Array.from(
          new Set(waveConfig.enemies.map((e) => e.type as EnemyKind)),
        );
        run.setWaveScene(archetype, hazardsPresent(hazardZones));
        run.startWave(waveNumber, kinds);
        run.showIntro({
          wave: waveNumber,
          archetype,
          hazards: hazardsPresent(hazardZones),
          worldModifier: run.worldModifierFor(waveNumber),
          isBoss: isBossWave(waveNumber),
          isElite: getWaveKind(waveNumber) === "elite",
        });
      }
    },

    initializeMultiplayerGame: (spawnPosition, playerColor, playerName, seed, serverTerrain, serverArenaSize, serverObstacles) => {
      // O terreno vive fora do React e não se apaga sozinho: sem esta limpeza,
      // uma poça de lava da partida anterior continuaria ativa aqui — e um
      // estado de fogo, grudado no tanque que acabou de entrar.
      clearArenaSurface();
      resetTankSurfaces();
      // Use o seed do servidor para gerar a arena de forma sincronizada
      const serverSeed = seed || 12345;
      
      // O servidor já resolve o terrain (inclusive "random" -> terrain específico)
      // Então serverTerrain sempre será um terrain válido (industrial, desert, etc.)
      let terrainToUse: TerrainConfig | undefined = undefined;
      if (serverTerrain) {
        const themeKey = serverTerrain as keyof typeof TERRAIN_THEMES;
        if (TERRAIN_THEMES[themeKey]) {
          terrainToUse = TERRAIN_THEMES[themeKey];
        }
      }
      
      const waveConfig = generateWaveConfig(1, terrainToUse, serverSeed);
      
      // Usa o tamanho da arena definido pelo servidor (baseado no terrain escolhido pelo host)
      const arenaSize = serverArenaSize || 40;
      const multiplayerArenaShape: ArenaShape = "square";
      
      // Sobrescreve as configurações de arena com os valores do servidor
      waveConfig.arenaSize = arenaSize;
      waveConfig.arenaShape = multiplayerArenaShape;
      
      // ---- Terreno ----
      // Netcode por semente, como a zona do Battle Royale é por parâmetros: o
      // terreno não muda durante a partida e é função pura de (semente, tamanho
      // da arena), então cada peer o gera sozinho e a arena sai idêntica para
      // todos — custo de banda zero. O servidor gera exatamente este mesmo
      // plano e é ele quem cobra o dano; o cliente cuida do movimento, dos
      // efeitos e do que se vê.
      const mpTerrain = planArenaTerrain({
        mode: "multiplayer",
        seed: serverSeed,
        arenaSize,
        // As mesmas áreas de nascimento que os obstáculos respeitam, com a
        // folga de um tanque: ninguém deve aparecer na beira de um lago sem ter
        // escolhido isso.
        safeSpots: multiplayerSpawnZones(arenaSize).map((zone) => ({
          ...zone,
          radius: zone.radius + MP_SPAWN_MARGIN,
        })),
      });
      const mpHazards = mpTerrain.zones;

      // [NETCODE] Obstáculos vêm do servidor (autoridade) — todos os clientes
      // e o servidor compartilham exatamente o mesmo layout e destruição.
      // Fallback: geração local por seed (compatibilidade com servidor antigo).
      let obstacles: ObstacleData[];
      if (serverObstacles && serverObstacles.length > 0) {
        // Já vêm sem os blocos que caíam dentro do terreno: quem filtra é o
        // servidor, e filtrar de novo aqui deixaria o cliente com menos blocos
        // do que a autoridade — uma bala pararia no ar para quem assiste.
        obstacles = serverObstacles;
      } else {
        const random = seededRandom(serverSeed + 10000);
        obstacles = generateArenaObstacles(waveConfig, random);
        if (mpHazards.length > 0) {
          obstacles = obstacles.filter(
            (obs) => !isInsideHazard(obs.position.x, obs.position.z, mpHazards, 0.8),
          );
        }
      }

      setArenaSurface(mpHazards, arenaSize);
      
      const selectedTankConfig = useProgress.getState().getSelectedTank();
      const selectedSkin = useProgress.getState().getSelectedSkin();
      const selectedEffect = useProgress.getState().getSelectedEffect();
      
      const playerTank: TankData = {
        id: "player",
        position: new THREE.Vector3(spawnPosition.x, spawnPosition.y, spawnPosition.z),
        rotation: 0,
        health: selectedTankConfig.health,
        maxHealth: selectedTankConfig.health,
        color: playerColor,
        isPlayer: true,
        isAlive: true,
        name: playerName,
        lastFireTime: 0,
        speed: selectedTankConfig.speed,
        damage: selectedTankConfig.damage,
        shotCount: selectedTankConfig.bulletCount ?? 1,
        size: selectedTankConfig.scale ?? 1,
        fireRate: selectedTankConfig.fireRate ?? 1,
        skinId: selectedSkin?.id || null,
        effectId: selectedEffect?.id || null,
      };
      
      set({
        phase: "playing",
        waveConfig,
        arenaSize: waveConfig.arenaSize,
        arenaShape: waveConfig.arenaShape,
        terrain: waveConfig.terrain,
        playerTank,
        enemyTanks: [],
        bullets: [],
        obstacles,
        items: [],
        explosions: [],
        heldItem: null,
        activeEffect: null,
        score: 0,
        wave: 1,
        killsThisWave: 0,
        isMultiplayer: true,
        moveDirection: new THREE.Vector2(0, 0),
        isFiring: false,
      });
    },
    
    // =============================================
    // MODO HISTÓRIA - Implementações
    // =============================================
    
    initializeStoryChapter: (chapterId: number, options?: { dungeonMode?: boolean }) => {
      // O terreno vive fora do React e não se apaga sozinho: sem esta limpeza,
      // uma poça de lava da partida anterior continuaria ativa aqui — e um
      // estado de fogo, grudado no tanque que acabou de entrar.
      clearArenaSurface();
      resetTankSurfaces();
      // [BESTIÁRIO] Novo capítulo: Fênix recupera o renascimento, fogos apagam
      set({ playerRebirthUsed: false, playerRebirthInvulnUntil: 0, lastRebirthAt: 0, enemyBurns: {} });
      const waveConfig = generateStoryArenaConfig(chapterId);
      const isDungeonMode = options?.dungeonMode ?? chapterHasDungeon(chapterId);
      const random = seededRandom(
        deterministicSeed("story_chapter", chapterId, isDungeonMode ? "dungeon" : "linear")
      );
      const obstacles = generateArenaObstacles(waveConfig, random);
      
      // Configurações especiais por capítulo
      const storyNoHealing = chapterId === 4; // Capítulo 4: sem itens de cura
      
      // Dungeons carregam inimigos por sala no loadDungeonRoom.
      const enemies = isDungeonMode
        ? []
        : generateStoryEnemies(chapterId, 0, obstacles, waveConfig.arenaSize);
      
      // Encontrar spawn seguro para o jogador
      const findSafePlayerSpawn = (): THREE.Vector3 => {
        const isBlocked = (pos: THREE.Vector3): boolean => {
          for (const obs of obstacles) {
            const dx = Math.abs(pos.x - obs.position.x);
            const dz = Math.abs(pos.z - obs.position.z);
            const safeDistance = Math.max(obs.size.x, obs.size.z) / 2 + 2.5;
            if (dx < safeDistance && dz < safeDistance) {
              return true;
            }
          }
          return false;
        };
        
        const spawnAttempts = [
          new THREE.Vector3(0, 0, waveConfig.arenaSize / 4),
          new THREE.Vector3(0, 0, 0),
          new THREE.Vector3(3, 0, 3),
          new THREE.Vector3(-3, 0, 3),
        ];
        
        for (const pos of spawnAttempts) {
          if (!isBlocked(pos)) return pos;
        }
        return new THREE.Vector3(0, 0, 0);
      };
      
      const progressState = useProgress.getState();
      const defaultStoryTankConfig =
        progressState.tanks.find((tank) => tank.id === "standard") ||
        progressState.getSelectedTank();
      
      const playerTank: TankData = {
        id: "player",
        position: findSafePlayerSpawn(),
        rotation: 0,
        health: defaultStoryTankConfig.health,
        maxHealth: defaultStoryTankConfig.health,
        color: defaultStoryTankConfig.color,
        isPlayer: true,
        isAlive: true,
        name: "Arthur Ren",
        lastFireTime: 0,
        speed: defaultStoryTankConfig.speed,
        damage: defaultStoryTankConfig.damage,
        shotCount: defaultStoryTankConfig.bulletCount ?? 1,
        size: defaultStoryTankConfig.scale ?? 1,
        fireRate: defaultStoryTankConfig.fireRate ?? 1,
        skinId: null,
        effectId: null,
      };
      
      const totalWaves = isDungeonMode ? 0 : getTotalWaves(chapterId);
      const firstWaveInfo = isDungeonMode ? null : getStoryWaveInfo(chapterId, 0);
      
      set({
        waveConfig,
        arenaSize: waveConfig.arenaSize,
        arenaShape: waveConfig.arenaShape,
        terrain: waveConfig.terrain,
        playerTank,
        enemyTanks: enemies,
        bullets: [],
        obstacles,
        explosions: [],
        items: [],
        heldItem: null,
        activeEffect: null,
        frozenEnemies: new Map(),
        score: 0,
        killsThisWave: 0,
        storyChapterId: chapterId,
        storyWaveIndex: 0,
        storyTotalWaves: totalWaves,
        storyBossPhase: 0,
        storyPendingDialogue: firstWaveInfo?.triggerDialogue || null,
        storyNoHealing,
        // Astra: verifica se já está desbloqueado permanentemente
        // Capítulos especiais podem desativar o Astra (ex: capítulo de desafio)
        astraUnlocked: useProgress.getState().astraPermanentlyUnlocked && chapterId !== 999, // 999 = capítulo sem Astra
        astraCharge: useProgress.getState().astraPermanentlyUnlocked ? 50 : 0, // Começa com 50% se já desbloqueado
        astraActive: false,
        astraActiveUntil: 0,
        astraCooldownUntil: 0,
        astraUsedThisChapter: 0,
        moveDirection: new THREE.Vector2(0, 0),
        isFiring: false,
      });
    },
    
    advanceStoryWave: (): string | null => {
      const { storyChapterId, storyWaveIndex, storyTotalWaves, obstacles, arenaSize } = get();
      
      if (storyChapterId === null) return null;
      
      const nextWaveIndex = storyWaveIndex + 1;
      
      // Verificar se terminou todas as waves
      if (nextWaveIndex >= storyTotalWaves) {
        return null; // Capítulo completo
      }
      
      // Obter info da próxima wave
      const nextWaveInfo = getStoryWaveInfo(storyChapterId, nextWaveIndex);
      if (!nextWaveInfo) return null;
      
      // Gerar inimigos da próxima wave
      const newEnemies = generateStoryEnemies(storyChapterId, nextWaveIndex, obstacles, arenaSize);
      
      set({
        storyWaveIndex: nextWaveIndex,
        enemyTanks: newEnemies,
        killsThisWave: 0,
        storyPendingDialogue: nextWaveInfo.triggerDialogue || null,
      });
      
      return nextWaveInfo.triggerDialogue || null;
    },
    
    spawnStoryWaveEnemies: () => {
      const { storyChapterId, storyWaveIndex, obstacles, arenaSize } = get();
      
      if (storyChapterId === null) return;
      
      const enemies = generateStoryEnemies(storyChapterId, storyWaveIndex, obstacles, arenaSize);
      
      set({
        enemyTanks: enemies,
        killsThisWave: 0,
      });
    },
    
    getStoryBossConfig: (): BossConfig | null => {
      const { storyChapterId } = get();
      if (storyChapterId === null) return null;
      return BOSS_CONFIGS[storyChapterId] || null;
    },
    
    advanceBossPhase: (): boolean => {
      const { storyChapterId, storyBossPhase, enemyTanks } = get();
      
      if (storyChapterId === null) return false;
      
      const bossConfig = BOSS_CONFIGS[storyChapterId];
      if (!bossConfig || bossConfig.phases <= 1) return false;
      
      const nextPhase = storyBossPhase + 1;
      if (nextPhase >= bossConfig.phases) return false;
      
      // Atualizar boss com stats da próxima fase
      if (bossConfig.phaseConfigs && bossConfig.phaseConfigs[nextPhase]) {
        const phaseConfig = bossConfig.phaseConfigs[nextPhase];
        
        const updatedEnemies = enemyTanks.map((enemy) => {
          if (enemy.id === bossConfig.id) {
            return {
              ...enemy,
              health: phaseConfig.health,
              maxHealth: phaseConfig.health,
              damage: phaseConfig.damage,
              shotCount: phaseConfig.shotCount,
              speed: phaseConfig.speed,
              isAlive: true,
            };
          }
          return enemy;
        });
        
        set({
          storyBossPhase: nextPhase,
          enemyTanks: updatedEnemies,
        });
        
        return true;
      }
      
      return false;
    },
    
    clearStoryPendingDialogue: () => {
      set({ storyPendingDialogue: null });
    },
    
    // =============================================
    // DUNGEON SYSTEM - Carregar sala
    // =============================================
    
    loadDungeonRoom: (room: DungeonRoom, waveIndex: number = 0, entryContext: DungeonRoomEntryContext | null = null) => {
      // O terreno vive fora do React e não se apaga sozinho: sem esta limpeza,
      // uma poça de lava da partida anterior continuaria ativa aqui — e um
      // estado de fogo, grudado no tanque que acabou de entrar.
      clearArenaSurface();
      resetTankSurfaces();
      const { storyChapterId } = get();

      // Obter configuração de arena para a sala
      const arenaConfig = getDungeonRoomArenaConfig(room);
      const random = seededRandom(
        deterministicSeed("dungeon_room_obstacles", storyChapterId ?? 0, room.id, room.arenaSize)
      );

      // Gerar novos obstáculos
      const obstacles = generateArenaObstacles(arenaConfig, random);

      // Calcular multiplicador de dificuldade baseado no capítulo
      const difficultyMultiplier = 1 + (storyChapterId || 1) * 0.1;

      // Salas não-combate (corredores, tesouro, descanso) nunca devem gerar inimigos.
      const isNonCombatRoom = room.type === "corridor" || room.type === "treasure" || room.type === "rest";

      const shouldSpawnEnemies = isNonCombatRoom
        ? false
        : shouldSpawnEnemiesInRoom({
            roomCompleted: entryContext?.roomCompleted,
            forceRespawn: entryContext?.forceRespawn,
          });

      // Salas já completadas não devem respawnar inimigos ao revisitar.
      const enemies = shouldSpawnEnemies
        ? generateDungeonRoomEnemies(room, waveIndex, obstacles, difficultyMultiplier)
        : [];

      // Reposicionar o jogador no spawn da nova sala.
      // IMPORTANTE: ler playerTank do store imediatamente antes do set()
      // para garantir que temos a versão mais recente.
      const { playerTank } = get();
      const boundary = room.arenaSize / 2 - 2;
      const configuredSpawn = room.playerSpawnPosition
        ? room.playerSpawnPosition.clone()
        : new THREE.Vector3(0, 0, Math.max(2, boundary * 0.7));
      const playerSpawn = getDungeonSpawnPosition(room, configuredSpawn, {
        fromRoomId: entryContext?.fromRoomId ?? null,
        entryDirection: entryContext?.entryDirection ?? null,
        isChapterStart: entryContext?.isChapterStart ?? false,
      });

      // Calcular rotação do jogador para olhar para o centro da sala
      // (efeito visual natural de "entrar olhando para frente")
      let spawnRotation = playerTank?.rotation ?? 0;
      if (entryContext?.entryDirection) {
        // Correção Trigonométrica: 0 aponta para NORTH (-Z). Math.PI aponta para SOUTH (+Z).
        // EAST (+X) -> vira para WEST (-X) => Math.PI / 2
        // WEST (-X) -> vira para EAST (+X) => -Math.PI / 2
        switch (entryContext.entryDirection) {
          case "SOUTH": spawnRotation = 0; break;
          case "NORTH": spawnRotation = Math.PI; break;
          case "EAST": spawnRotation = Math.PI / 2; break;
          case "WEST": spawnRotation = -Math.PI / 2; break;
        }
      }

      set({
        arenaSize: room.arenaSize,
        arenaShape: room.arenaShape,
        terrain: room.terrainTheme,
        obstacles,
        enemyTanks: enemies,
        bullets: [],
        items: [],
        explosions: [],
        killsThisWave: 0,
        // Resetar estado do wave para evitar interferência
        storyTotalWaves: 0,
        storyWaveIndex: 0,
        storyPendingDialogue: null,
        // Estado do dungeon
        currentDungeonRoomId: room.id,
        dungeonEntryDirection: entryContext?.entryDirection || null,
        dungeonRoomWaveIndex: waveIndex,
        dungeonDoorsUnlocked: !shouldSpawnEnemies || isNonCombatRoom,
        // Atualizar posição e rotação do jogador
        playerTank: playerTank ? {
          ...playerTank,
          position: playerSpawn,
          rotation: spawnRotation,
        } : null,
      });

      // Marcar sala como visitada
      const { dungeonVisitedRooms } = get();
      if (!dungeonVisitedRooms.includes(room.id)) {
        set({ dungeonVisitedRooms: [...dungeonVisitedRooms, room.id] });
      }

      console.log(
        `[Dungeon] Sala "${room.name}" carregada - spawn=(${playerSpawn.x.toFixed(1)}, ${playerSpawn.z.toFixed(1)}), ${enemies.length} inimigos, wave ${waveIndex + 1}, entry=${entryContext?.entryDirection ?? "none"}, completed=${entryContext?.roomCompleted ? "yes" : "no"}`
      );
    },
    
    // Avança para próxima wave dentro da sala atual
    advanceDungeonWave: () => {
      const { currentDungeonRoomId, dungeonRoomWaveIndex, storyChapterId } = get();
      
      if (!currentDungeonRoomId || !storyChapterId) return false;
      
      // Importar dinamicamente para evitar dependência circular
      const { getDungeonConfig } = require("../story/dungeonConfigs");
      const dungeonConfig = getDungeonConfig(storyChapterId);
      
      if (!dungeonConfig) return false;
      
      const currentRoom = dungeonConfig.rooms.find((r: DungeonRoom) => r.id === currentDungeonRoomId);
      if (!currentRoom) return false;
      
      // Verificar se há mais waves na sala
      const totalWaves = currentRoom.waves?.length || 1;
      const nextWaveIndex = dungeonRoomWaveIndex + 1;
      
      if (nextWaveIndex < totalWaves) {
        // Carregar próxima wave
        const { obstacles } = get();
        const difficultyMultiplier = 1 + storyChapterId * 0.1;
        const enemies = generateDungeonRoomEnemies(currentRoom, nextWaveIndex, obstacles, difficultyMultiplier);
        
        set({
          dungeonRoomWaveIndex: nextWaveIndex,
          enemyTanks: enemies,
          killsThisWave: 0,
        });
        
        console.log(`[Dungeon] Wave ${nextWaveIndex + 1}/${totalWaves} iniciada`);
        return true;
      }
      
      // Todas as waves completadas - desbloquear portas
      set({ dungeonDoorsUnlocked: true });
      console.log(`[Dungeon] Sala "${currentRoom.name}" completada! Portas desbloqueadas.`);
      return false;
    },
    
    // Desbloqueia as portas manualmente (para salas sem combate)
    unlockDungeonDoors: () => {
      set({ dungeonDoorsUnlocked: true });
    },
    
    // Entra em uma nova sala do dungeon
    enterDungeonRoom: (roomId: string) => {
      const { storyChapterId } = get();
      
      if (!storyChapterId) return;
      
      const { getDungeonConfig } = require("../story/dungeonConfigs");
      const dungeonConfig = getDungeonConfig(storyChapterId);
      
      if (!dungeonConfig) return;
      
      const room = dungeonConfig.rooms.find((r: DungeonRoom) => r.id === roomId);
      if (!room) {
        console.warn(`[Dungeon] Sala "${roomId}" não encontrada!`);
        return;
      }
      
      // Usar a ação loadDungeonRoom
      get().loadDungeonRoom(room, 0);
    },
    
    // Reseta o estado do dungeon (usado ao iniciar novo capítulo)
    resetDungeonState: () => {
      set({
        currentDungeonRoomId: null,
        dungeonRoomWaveIndex: 0,
        dungeonDoorsUnlocked: false,
        dungeonVisitedRooms: [],
        dungeonCollectedRewards: [],
      });
    },
    
    // Verifica se a sala atual foi completada (todos inimigos mortos)
    isDungeonRoomCleared: () => {
      const { enemyTanks } = get();
      return enemyTanks.length === 0;
    },
    
    // =============================================
    // TIMER DE PROTEÇÃO - Capítulo 3
    // =============================================
    
    // Inicia o timer de proteção (chamado quando a missão de proteção começa)
    startProtectionTimer: (duration: number, target: string, dialogueTriggers?: { time: number; nodeId: string }[]) => {
      const now = Date.now();
      set({
        storyProtectionActive: true,
        storyProtectionTimeLeft: duration,
        storyProtectionStartTime: now,
        storyProtectionDuration: duration,
        storyProtectionTarget: target,
        storyProtectionDialogueTriggers: dialogueTriggers || [],
      });
    },
    
    // Atualiza o timer de proteção (chamado no game loop)
    updateProtectionTimer: (): { completed: boolean; dialogueToShow: string | null } => {
      const { 
        storyProtectionActive, 
        storyProtectionStartTime, 
        storyProtectionDuration,
        storyProtectionDialogueTriggers 
      } = get();
      
      if (!storyProtectionActive) {
        return { completed: false, dialogueToShow: null };
      }
      
      const now = Date.now();
      const elapsed = (now - storyProtectionStartTime) / 1000; // em segundos
      const timeLeft = Math.max(0, storyProtectionDuration - elapsed);
      
      // Verifica se algum diálogo deve ser ativado
      let dialogueToShow: string | null = null;
      const remainingTriggers = storyProtectionDialogueTriggers.filter(trigger => {
        // Dispara quando o tempo restante cruza o threshold
        const shouldTrigger = timeLeft <= trigger.time && timeLeft > trigger.time - 0.5;
        if (shouldTrigger) {
          dialogueToShow = trigger.nodeId;
        }
        return timeLeft > trigger.time; // Mantém triggers futuros
      });
      
      // Atualiza estado
      if (timeLeft <= 0) {
        // Timer completo! Proteção bem-sucedida
        set({
          storyProtectionActive: false,
          storyProtectionTimeLeft: 0,
          storyProtectionDialogueTriggers: [],
        });
        return { completed: true, dialogueToShow: "protection_complete" };
      }
      
      set({
        storyProtectionTimeLeft: timeLeft,
        storyProtectionDialogueTriggers: remainingTriggers,
      });
      
      return { completed: false, dialogueToShow };
    },
    
    // Para o timer de proteção manualmente
    stopProtectionTimer: () => {
      set({
        storyProtectionActive: false,
        storyProtectionTimeLeft: 0,
        storyProtectionStartTime: 0,
        storyProtectionDuration: 0,
        storyProtectionTarget: null,
        storyProtectionDialogueTriggers: [],
      });
    },
    
    // =============================================
    // MODO ASTRA - Implementações
    // =============================================
    
    // Desbloqueia o Modo Astra (chamado quando entra na boss wave pela primeira vez)
    unlockAstra: () => {
      // Desbloqueia nesta sessão
      set({ 
        astraUnlocked: true,
        astraCharge: 50, // Começa com 50% de carga para dar chance ao jogador
      });
      
      // Salva permanentemente no progresso do jogador
      useProgress.getState().unlockAstraPermanently();
    },
    
    // Atualiza a carga do Astra com o tempo (chamado no game loop)
    updateAstraCharge: (delta: number) => {
      const { storyChapterId, astraUnlocked, astraActive, astraCooldownUntil, astraCharge } = get();
      
      // Só funciona no modo história e quando desbloqueado
      if (storyChapterId === null || !astraUnlocked) return;
      
      // Se Astra está ativo, não carrega
      if (astraActive) return;
      
      // Se está em cooldown (superaquecido), não carrega
      const now = Date.now();
      if (now < astraCooldownUntil) return;
      
      // Taxa de carga: ~20 segundos para carga completa (5% por segundo)
      const chargeRate = 5 * delta;
      const newCharge = Math.min(100, astraCharge + chargeRate);
      
      if (newCharge !== astraCharge) {
        set({ astraCharge: newCharge });
      }
    },
    
    // Ativa o Modo Astra se disponível
    activateAstra: (): boolean => {
      const { storyChapterId, astraCharge, astraActive, astraCooldownUntil, astraUsedThisChapter } = get();
      
      // Verificações
      if (storyChapterId === null) return false;
      if (astraActive) return false;
      if (astraCharge < 100) return false;
      
      const now = Date.now();
      if (now < astraCooldownUntil) return false;
      
      // Duração do Modo Astra: 4 segundos
      const ASTRA_DURATION = 4000;
      
      set({
        astraActive: true,
        astraActiveUntil: now + ASTRA_DURATION,
        astraCharge: 0,
        astraUsedThisChapter: astraUsedThisChapter + 1,
      });
      
      return true;
    },
    
    // Desativa o Modo Astra (chamado quando tempo acaba ou manualmente)
    deactivateAstra: () => {
      const { astraActive } = get();
      if (!astraActive) return;
      
      // Cooldown de superaquecimento: 15 segundos
      const ASTRA_COOLDOWN = 15000;
      const now = Date.now();
      
      set({
        astraActive: false,
        astraActiveUntil: 0,
        astraCooldownUntil: now + ASTRA_COOLDOWN,
        astraCharge: 0,
      });
    },
    
    // Verifica se o Astra está pronto para uso
    isAstraReady: (): boolean => {
      const { storyChapterId, astraCharge, astraActive, astraCooldownUntil } = get();
      if (storyChapterId === null) return false;
      if (astraActive) return false;
      if (Date.now() < astraCooldownUntil) return false;
      return astraCharge >= 100;
    },
    
    // Verifica se está em cooldown
    isAstraOnCooldown: (): boolean => {
      const { astraCooldownUntil } = get();
      return Date.now() < astraCooldownUntil;
    },
    
    // Retorna percentual do cooldown restante (0-1)
    getAstraCooldownPercent: (): number => {
      const { astraCooldownUntil } = get();
      const now = Date.now();
      if (now >= astraCooldownUntil) return 0;
      
      const ASTRA_COOLDOWN = 15000;
      const remaining = astraCooldownUntil - now;
      return remaining / ASTRA_COOLDOWN;
    },
    
    // ============================================
    // HABILIDADES DE BOSS
    // ============================================
    
    // Adiciona uma mina no chão
    addBossMine: (mine) => {
      set((state) => ({
        bossMines: [...state.bossMines, mine],
      }));
    },
    
    // Remove uma mina
    removeBossMine: (id) => {
      set((state) => ({
        bossMines: state.bossMines.filter((m) => m.id !== id),
      }));
    },
    
    // Dispara uma mina (dano ao jogador se estiver perto)
    triggerMine: (id) => {
      const { bossMines, playerTank, updatePlayerHealth, addExplosion } = get();
      const mine = bossMines.find((m) => m.id === id);
      if (!mine || !playerTank) return;
      
      // Adicionar explosão visual
      addExplosion({ x: mine.position.x, y: 0.5, z: mine.position.z }, "#ff4500");
      
      // Verificar se jogador está no raio
      const dx = playerTank.position.x - mine.position.x;
      const dz = playerTank.position.z - mine.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      
      if (dist < mine.triggerRadius) {
        updatePlayerHealth(playerTank.health - mine.damage);
      }
      
      // Remover mina
      get().removeBossMine(id);
    },
    
    // Adiciona um rastro de gelo
    addBossFreezePath: (path) => {
      set((state) => ({
        bossFreezePaths: [...state.bossFreezePaths, path],
      }));
    },
    
    // Atualiza rastro de gelo (adiciona ponto)
    updateBossFreezePath: (bossId, newPoint) => {
      set((state) => ({
        bossFreezePaths: state.bossFreezePaths.map((p) =>
          p.bossId === bossId
            ? { ...p, path: [...p.path, newPoint.clone()] }
            : p
        ),
      }));
    },
    
    // Adiciona área de neblina
    addBossFogArea: (fog) => {
      set((state) => ({
        bossFogAreas: [...state.bossFogAreas, fog],
      }));
    },
    
    // Desabilita tiro do jogador (EMP)
    setPlayerEmpDisabled: (until) => {
      set({ playerEmpDisabledUntil: until });
    },
    
    // Verifica se jogador está com EMP ativo
    isPlayerEmpDisabled: () => {
      return Date.now() < get().playerEmpDisabledUntil;
    },
    
    // Define boss em estado de carregamento de habilidade
    setBossCharging: (charging) => {
      set({ bossCharging: charging });
    },
    
    // Limpa todas as habilidades de boss (fim de wave/capítulo)
    cleanupBossAbilities: () => {
      set({
        bossAbilities: [],
        bossMines: [],
        bossFreezePaths: [],
        bossFogAreas: [],
        playerEmpDisabledUntil: 0,
        bossCharging: null,
      });
    },
    
    // Verifica se jogador está em rastro de gelo
    isPlayerOnFreezePath: () => {
      const { playerTank, bossFreezePaths } = get();
      if (!playerTank) return false;
      
      const now = Date.now();
      const activePaths = bossFreezePaths.filter((p) => now < p.expiresAt);
      
      for (const freezePath of activePaths) {
        for (let i = 0; i < freezePath.path.length - 1; i++) {
          const p1 = freezePath.path[i];
          const p2 = freezePath.path[i + 1];
          
          // Distância do jogador ao segmento
          const dx = p2.x - p1.x;
          const dz = p2.z - p1.z;
          const lenSq = dx * dx + dz * dz;
          if (lenSq === 0) continue;
          
          const t = Math.max(0, Math.min(1,
            ((playerTank.position.x - p1.x) * dx + (playerTank.position.z - p1.z) * dz) / lenSq
          ));
          
          const projX = p1.x + t * dx;
          const projZ = p1.z + t * dz;
          
          const distSq = 
            (playerTank.position.x - projX) ** 2 + 
            (playerTank.position.z - projZ) ** 2;
          
          if (distSq < (freezePath.width * freezePath.width) / 4) {
            return true;
          }
        }
      }
      return false;
    },
    
    // Verifica se jogador está em área de neblina
    isPlayerInFog: () => {
      const { playerTank, bossFogAreas } = get();
      if (!playerTank) return false;
      
      const now = Date.now();
      return bossFogAreas.some((fog) => {
        if (now >= fog.expiresAt) return false;
        const dx = playerTank.position.x - fog.position.x;
        const dz = playerTank.position.z - fog.position.z;
        return (dx * dx + dz * dz) < (fog.radius * fog.radius);
      });
    },
    
    // Retorna fator de lentidão (1 = normal, 0.4 = 40% da velocidade)
    getPlayerSlowFactor: () => {
      const { bossFreezePaths, playerTank } = get();
      if (!playerTank) return 1;
      
      const now = Date.now();
      const activePaths = bossFreezePaths.filter((p) => now < p.expiresAt);
      
      // Verificar se está em algum rastro de gelo
      for (const freezePath of activePaths) {
        for (let i = 0; i < freezePath.path.length - 1; i++) {
          const p1 = freezePath.path[i];
          const p2 = freezePath.path[i + 1];
          
          const dx = p2.x - p1.x;
          const dz = p2.z - p1.z;
          const lenSq = dx * dx + dz * dz;
          if (lenSq === 0) continue;
          
          const t = Math.max(0, Math.min(1,
            ((playerTank.position.x - p1.x) * dx + (playerTank.position.z - p1.z) * dz) / lenSq
          ));
          
          const projX = p1.x + t * dx;
          const projZ = p1.z + t * dz;
          
          const distSq = 
            (playerTank.position.x - projX) ** 2 + 
            (playerTank.position.z - projZ) ** 2;
          
          if (distSq < (freezePath.width * freezePath.width) / 4) {
            return freezePath.slowFactor;
          }
        }
      }
      
      return 1;
    },
  }))
);
